import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useNavigate } from 'react-router-dom';
import { useSession } from '@/lib/SessionContext';
import { useKurs } from '@/hooks/useKurs';
import { FileText, Search, Truck, PackageCheck, X, ShoppingCart, CheckCircle, ChevronDown, ChevronUp, Check, Save, Loader2, Sparkles, Printer, Pencil, Bot, Package2, Wallet, Send, CreditCard, Building2, ChevronRight, AlertTriangle, CheckSquare, Square, Clock, Timer, Trash2, PenTool } from 'lucide-react';
import POPrint from '@/components/purchasing/POPrint';
import { generatePONumber } from '@/lib/poNumberGenerator';
import { toast } from 'react-hot-toast';
import { useClientPOs } from '@/hooks/useClientPOs';
import AsaAssistant from '@/components/shared/AsaAssistant';
import { getShowPriceAccess } from '@/lib/AuthSession';
import CustomDatePicker from '@/components/shared/CustomDatePicker';
import AsaArrivalConfirmModal from '@/components/purchasing/AsaArrivalConfirmModal';

const STATUS_LIST = [
  'Harus Dibeli',
  'Dalam Permintaan',
  'Sudah Dibayar',
  'Dalam Pengiriman',
  'Sudah Datang'
];

// Helper: Generate reference number SDE-BELI-(kategori)-(bulan)-(tanggal)
const generateRefNumber = (item) => {
  const now = new Date();
  const bulan = String(now.getMonth() + 1).padStart(2, '0');
  const tanggal = String(now.getDate()).padStart(2, '0');
  // Map type to readable category label
  const typeMap = {
    'botol': 'Botol',
    'tutup': 'Tutup',
    'spray': 'Spray',
    'bahan-cair': (item.kategori || item.nama || 'BahanCair').replace(/\s+/g, ''),
    'item-tambahan': (item.kategori || item.nama || 'ItemTambahan').replace(/\s+/g, ''),
  };
  const kategori = typeMap[item._type] || item._type || 'Lainnya';
  return `SDE-BELI-${kategori}-${bulan}-${tanggal}`;
};

// Helper: Check if a PurchasingRequest matches a material item
const isRequestMatchingItem = (req, itemId, itemType) => {
  if (String(req.material_id) === String(itemId) && String(req.material_type) === String(itemType)) {
    return true;
  }
  if (req.batch_items) {
    try {
      const parsed = typeof req.batch_items === 'string' ? JSON.parse(req.batch_items) : req.batch_items;
      if (Array.isArray(parsed)) {
        return parsed.some(b => String(b.id) === String(itemId) && String(b.type) === String(itemType));
      }
    } catch (e) {
      // fallback if parsing fails
      const idStr = String(itemId);
      const typeStr = String(itemType);
      return (
        req.batch_items.includes(`"id":"${idStr}"`) || 
        req.batch_items.includes(`"id":${idStr}`)
      ) && (
        req.batch_items.includes(`"type":"${typeStr}"`) ||
        req.batch_items.includes(`"type":${typeStr}`)
      );
    }
  }
  return false;
};


// Helper: Get unique item key
const getItemKey = (item) => {
  if (!item) return '';
  return item._ticket ? `ticket_${item._ticket.id}` : `${item._type || item.type || 'item'}_${item.id || item.nama || item.name}`;
};

export default function PurchasingItemsPO() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [showAsa, setShowAsa] = useState(true);
  const { member, companySettings } = useSession();
  const { kurs, kursEuro } = useKurs();
  const showPrice = getShowPriceAccess(member, "purchasing_items") || getShowPriceAccess(member, "portal_purchasing");

  // ── Batch Selection State ──
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [isSelectionMode, setIsSelectionMode] = useState(false);

  // ── Ignored Items State (Persisted) ──
  const [ignoredKeys, setIgnoredKeys] = useState(() => {
    try {
      const saved = localStorage.getItem('sde_purchasing_po_ignored_items');
      return saved ? new Set(JSON.parse(saved)) : new Set();
    } catch (e) {
      return new Set();
    }
  });

  // ── Qty Input State ── key: itemKey, value: { qty, confirmed }
  const [qtyInputs, setQtyInputs] = useState({});

  // ── Batch Fill State ──
  const [batchFillQty, setBatchFillQty] = useState('');

  // ── Pengajuan Modal State ──
  const [showPengajuanModal, setShowPengajuanModal] = useState(false);
  const [showPrintPopup, setShowPrintPopup] = useState(null); // Data for print modal
  const [printData, setPrintData] = useState(null);
  const [pengajuanItem, setPengajuanItem] = useState(null);
  const [isSubmittingPengajuan, setIsSubmittingPengajuan] = useState(false);
  const [pengajuanBank, setPengajuanBank] = useState('BCA');
  const [pengajuanNoRek, setPengajuanNoRek] = useState('');
  const [pengajuanNamaRek, setPengajuanNamaRek] = useState('');
  const [pengajuanNamaSales, setPengajuanNamaSales] = useState('');
  const [pengajuanNamaPerusahaan, setPengajuanNamaPerusahaan] = useState('');
  const [pengajuanNPWP, setPengajuanNPWP] = useState('');
  const [pengajuanSupplierPhone, setPengajuanSupplierPhone] = useState('');
  const [pengajuanSupplierAddress, setPengajuanSupplierAddress] = useState('');
  const [pengajuanMetode, setPengajuanMetode] = useState('Cash');
  const [pengajuanTempo, setPengajuanTempo] = useState('');
  const [pengajuanOngkirMode, setPengajuanOngkirMode] = useState('transfer'); // 'transfer' | 'petty_cash'
  const [pengajuanOngkir, setPengajuanOngkir] = useState('');
  const [pengajuanCatatan, setPengajuanCatatan] = useState('');
  
  // Data Perusahaan Pemesan & Syarat PO (Untuk PO PDF)
  const [pengajuanPemesanNama, setPengajuanPemesanNama] = useState('');
  const [pengajuanPemesanNPWP, setPengajuanPemesanNPWP] = useState('');
  const [pengajuanPemesanAlamat, setPengajuanPemesanAlamat] = useState('');
  const [pengajuanPemesanPIC, setPengajuanPemesanPIC] = useState('');
  const [pengajuanPemesanTelp, setPengajuanPemesanTelp] = useState('');

  const PREDEFINED_SYARAT_OPTIONS = [
    "Barang harus sesuai dengan spesifikasi dan jumlah yang tertera.",
    "Mohon sertakan COA (Certificate of Analysis) pada setiap pengiriman.",
    "Pengiriman ke gudang utama PT Satala Dermatech Essential.",
    "Mohon konfirmasi penerimaan PO ini.",
    "Sertakan Sertifikat Halal & MSDS pada setiap pengiriman.",
    "Pengiriman wajib melampirkan Surat Jalan fisik asli."
  ];

  const [pengajuanSyaratList, setPengajuanSyaratList] = useState([
    "Barang harus sesuai dengan spesifikasi dan jumlah yang tertera.",
    "Mohon sertakan COA (Certificate of Analysis) pada setiap pengiriman.",
    "Pengiriman ke gudang utama PT Satala Dermatech Essential.",
    "Mohon konfirmasi penerimaan PO ini."
  ]);
  const [customSyaratInput, setCustomSyaratInput] = useState('');
  
  const [supplierSearch, setSupplierSearch] = useState('');
  const [showSupplierDropdown, setShowSupplierDropdown] = useState(false);
  const [pemesanSearch, setPemesanSearch] = useState('');
  const [showPemesanDropdown, setShowPemesanDropdown] = useState(false);
  
  // ── Main Page Filters ──
  const [selectedSupplierFilter, setSelectedSupplierFilter] = useState('all');
  const [mainSearchQuery, setMainSearchQuery] = useState('');
  const [selectedProgressItem, setSelectedProgressItem] = useState(null);

  // ── Batch Status Change Modal ──
  const [showBatchStatusModal, setShowBatchStatusModal] = useState(false);
  const [batchTargetStatus, setBatchTargetStatus] = useState('');
  const [isBatchProcessing, setIsBatchProcessing] = useState(false);

  // ── Success Popup ──
  const [showSuccessPopup, setShowSuccessPopup] = useState(null);

  // ── Pagination & Scroll State ──
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  // ── Ekspedisi Modal (untuk input sebelum "Sudah Datang") ──
  const [showEkspedisiModal, setShowEkspedisiModal] = useState(false);
  const [ekspedisiItem, setEkspedisiItem] = useState(null); // single item or array
  const [ekspedisiNama, setEkspedisiNama] = useState('');
  const [isProcessingArrival, setIsProcessingArrival] = useState(false);

  // 1. Fetch Active POs & Production Orders
  const { clientPOs = [], isLoading: loadingPOs } = useClientPOs();
  const activePOs = useMemo(() => clientPOs.filter(po => {
    const st = po.statusProduksi || '';
    return !['PESANAN SELESAI', 'SELESAI', 'BATAL', 'SUDAH SAMPAI', 'DIKIRIM', 'DIKIRIM SEBAGIAN'].includes(st.toUpperCase());
  }), [clientPOs]);
  const { data: productionOrders = [], isLoading: loadingProdOrders } = useQuery({
    queryKey: ['production-orders'],
    queryFn: () => base44.entities.ProductionOrder.list()
  });

  // 2. Fetch Database Bahan
  const { data: dbBahan = [], isLoading: loadingDbBahan } = useQuery({
    queryKey: ['database-bahan'],
    queryFn: () => base44.entities.DatabaseBahan.list()
  });

  // 3. Fetch Master Materials
  const { data: botolList = [] } = useQuery({ queryKey: ['botol'], queryFn: () => base44.entities.Botol.list() });
  const { data: tutupList = [] } = useQuery({ queryKey: ['tutup'], queryFn: () => base44.entities.Tutup.list() });
  const { data: sprayList = [] } = useQuery({ queryKey: ['spray'], queryFn: () => base44.entities.Spray.list() });
  const { data: fullsetList = [] } = useQuery({ queryKey: ['fullset'], queryFn: () => base44.entities.Fullset.list() });
  const { data: itemTambahanList = [] } = useQuery({ queryKey: ['item-tambahan'], queryFn: () => base44.entities.ItemTambahan.list() });
  const { data: bahanCairList = [] } = useQuery({ queryKey: ['bahan-cair'], queryFn: () => base44.entities.BahanCair.list() });

  // 4. Fetch Purchasing Tickets
  const { data: tickets = [], isLoading: loadingTickets } = useQuery({
    queryKey: ['purchasing-tickets'],
    queryFn: () => base44.entities.PurchasingTicket.list()
  });

  // 5. Fetch Supplier Database
  const { data: suppliers = [] } = useQuery({
    queryKey: ['supplier-database'],
    queryFn: async () => {
      try {
        const res = await base44.entities.SupplierDatabase?.list();
        return res || [];
      } catch (err) {
        return [];
      }
    }
  });

  // 6. Fetch Purchasing Requests
  const { data: requests = [] } = useQuery({
    queryKey: ['purchasing-requests'],
    queryFn: () => base44.entities.PurchasingRequest.list('-created_date')
  });

  const updateTicketMut = useMutation({
    mutationFn: ({ id, data }) => base44.entities.PurchasingTicket.update(id, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['purchasing-tickets'] })
  });
  const deleteTicketMut = useMutation({
    mutationFn: (id) => base44.entities.PurchasingTicket.hardDelete(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['purchasing-tickets'] })
  });

  // Mutations to update stock
  const updateBotolMut = useMutation({ mutationFn: ({ id, data }) => base44.entities.Botol.update(id, data), onSuccess: () => queryClient.invalidateQueries({ queryKey: ['botol'] }) });
  const updateTutupMut = useMutation({ mutationFn: ({ id, data }) => base44.entities.Tutup.update(id, data), onSuccess: () => queryClient.invalidateQueries({ queryKey: ['tutup'] }) });
  const updateSprayMut = useMutation({ mutationFn: ({ id, data }) => base44.entities.Spray.update(id, data), onSuccess: () => queryClient.invalidateQueries({ queryKey: ['spray'] }) });
  const { data: dataPemesanList = [] } = useQuery({
    queryKey: ['data-pemesan'],
    queryFn: async () => {
      try {
        const res = await base44.entities.DataPemesan?.list();
        return res || [];
      } catch (err) {
        return [];
      }
    }
  });

  const updateItemMut = useMutation({ mutationFn: ({ id, data }) => base44.entities.ItemTambahan.update(id, data), onSuccess: () => queryClient.invalidateQueries({ queryKey: ['item-tambahan'] }) });
  const updateBahanCairMut = useMutation({ mutationFn: ({ id, data }) => base44.entities.BahanCair.update(id, data), onSuccess: () => queryClient.invalidateQueries({ queryKey: ['bahan-cair'] }) });

  // ── CALCULATE DEFICITS ──
  const deficits = useMemo(() => {
    const required = {};

    // 1. Kumpulkan dan proses semua PO Produksi aktif
    const activeProductionOrders = productionOrders.filter(po => {
      if (po.deleted_at) return false;
      const st = (po.status || '').toLowerCase();
      return !['done', 'selesai', 'shipped', 'dikirim', 'batal', 'cancelled'].includes(st);
    });

    const norm = (s) => String(s || '').replace(/^(PO-|INV-|SPH-)/i, '').trim().toLowerCase();
    const processedPOKeys = new Set();

    activeProductionOrders.forEach(po => {
      if (po.order_number) {
        processedPOKeys.add(String(po.order_number).trim());
        processedPOKeys.add(norm(po.order_number));
      }
      if (po.id) {
        processedPOKeys.add(String(po.id).trim());
        processedPOKeys.add(norm(po.id));
      }
      if (po.no_po) {
        processedPOKeys.add(String(po.no_po).trim());
        processedPOKeys.add(norm(po.no_po));
      }
      if (po.brand_name) {
        processedPOKeys.add(`brand_${norm(po.brand_name)}`);
      }

      let brandName = po.brand_name || po.customerName || po.client || 'Unknown Brand';
      if (brandName.includes(' / ')) {
        brandName = brandName.split(' / ')[0].trim();
      }

      const STAGES_PAST_RACIK = ['diracik', 'racik', 'mixing', 'maserasi', 'menunggu_filling', 'filling', 'menunggu_packing', 'packing', 'siap_kirim', 'done', 'selesai'];
      const STAGES_PAST_FILLING = ['menunggu_packing', 'packing', 'siap_kirim', 'done', 'selesai'];
      const STAGES_PAST_PACKING = ['siap_kirim', 'done', 'selesai'];

      const st = String(po.status || '').toLowerCase().trim();
      const isRacikDone = STAGES_PAST_RACIK.includes(st) || 
                          po.task_stock_deducted?.racik === true || 
                          po.is_racik_done === true;
      const isFillingDone = STAGES_PAST_FILLING.includes(st) || 
                            po.task_stock_deducted?.filling === true;
      const isPackingDone = STAGES_PAST_PACKING.includes(st) || 
                            po.task_stock_deducted?.packaging === true;

      const addRequired = (key, type, id, qty) => {
        let needsMaterial = true;
        if (type === 'bahan-cair' && isRacikDone) needsMaterial = false;
        if (['botol', 'tutup', 'spray', 'fullset'].includes(type) && isFillingDone) needsMaterial = false;
        if (['item-tambahan', 'tambahan'].includes(type) && isPackingDone) needsMaterial = false;

        if (!needsMaterial || qty <= 0) return;
        if (!required[key]) required[key] = { type, id, qty: 0, fromProductionOrder: true, brands: new Set() };
        required[key].qty += qty;
        required[key].brands.add(brandName);
      };

      if (po.components && Array.isArray(po.components)) {
        po.components.forEach(comp => {
          let invId = comp.inventory_item_id;
          if (!invId && comp.item_name) {
            const foundFullset = fullsetList.find(f => f.nama === comp.item_name || (f.item_name && f.item_name === comp.item_name));
            if (foundFullset) invId = `fullset-${foundFullset.id}`;
            else {
              const foundBotol = botolList.find(b => b.nama === comp.item_name || (b.item_name && b.item_name === comp.item_name));
              if (foundBotol) invId = `botol-${foundBotol.id}`;
            }
          }

          if (invId) {
            let type = (comp.category === 'bibit' || comp.category === 'bahan_cair') ? 'bahan-cair' : comp.category;
            let cleanId = invId;
            const prefixMatch = invId.match(/^(botol-|tutup-|spray-|bahan-|tambahan-|fullset-)/);
            if (prefixMatch) {
              const prefix = prefixMatch[0].replace('-', '');
              cleanId = invId.replace(prefixMatch[0], '');
              if (prefix === 'fullset') type = 'fullset';
              else if (prefix === 'bahan') type = 'bahan-cair';
              else if (prefix === 'tambahan') type = 'item-tambahan';
              else type = prefix;
            }
            const key = `${type}_${cleanId}`;
            addRequired(key, type, cleanId, Number(comp.qty_needed) || 0);
          }
        });
      }

      const existingBahanIds = new Set(
        (po.components || [])
          .filter(c => c.category === 'bibit' || c.category === 'bahan_cair')
          .map(c => c.inventory_item_id)
          .filter(Boolean)
      );

      (po.products || []).forEach(prod => {
        const qty = Number(prod.target_qty || prod.quantity || 0);
        const ukuranMl = Number(prod.ukuran_botol_ml || prod.botol_ukuran_aktual || 30);
        const prodRacikan = prod.racikan_digunakan || prod.racikan || [];

        prodRacikan.forEach(r => {
          const namaLower = String(r.nama_bahan || r.nama || '').toLowerCase().trim();
          if (!namaLower) return;
          const matchedBahan = bahanCairList.find(b => b.nama && String(b.nama).toLowerCase().trim() === namaLower);
          if (matchedBahan) {
            const invId = `bahan-${matchedBahan.id}`;
            if (existingBahanIds.has(invId)) return;
            const cleanId = String(matchedBahan.id);
            const key = `bahan-cair_${cleanId}`;
            const persentase = Number(String(r.persentase || 0).replace(',', '.')) || 0;
            const mlNeeded = (persentase / 100) * ukuranMl * qty;
            const unit = String(matchedBahan.satuan || '').toLowerCase();
            const isKgOrL = ['kg', 'liter', 'l'].includes(unit);
            const qtyNeeded = isKgOrL ? mlNeeded / 1000 : mlNeeded;
            addRequired(key, 'bahan-cair', cleanId, qtyNeeded);
          }
        });
      });
    });

    // 2. ClientPO mandiri
    activePOs.forEach(po => {
      const poKey = String(po.id || '').trim();
      const poNo = String(po.no_po || po.invoice_number || '').trim();
      const bName = String(po.brand_name || po.customerName || '').trim();

      if (
        processedPOKeys.has(poKey) || 
        processedPOKeys.has(norm(poKey)) || 
        (poNo && (processedPOKeys.has(poNo) || processedPOKeys.has(norm(poNo)))) ||
        (bName && processedPOKeys.has(`brand_${norm(bName)}`))
      ) {
        return;
      }

      let brandName = po.customerName || po.brand_name || po.client || 'Unknown Brand';
      if (brandName.includes(' / ')) {
        brandName = brandName.split(' / ')[0].trim();
      }
      const addRequired = (key, type, id, qty) => {
        if (qty <= 0) return;
        if (!required[key]) required[key] = { type, id, qty: 0, fromProductionOrder: false, brands: new Set() };
        required[key].qty += qty;
        required[key].brands.add(brandName);
      };

      if (po.components && Array.isArray(po.components)) {
        po.components.forEach(comp => {
          if (comp.inventory_item_id) {
            const cleanId = comp.inventory_item_id.replace(/^(botol-|tutup-|spray-|bahan-|tambahan-|fullset-)/, '');
            const type = (comp.category === 'bibit' || comp.category === 'bahan_cair') ? 'bahan-cair' : comp.category;
            const key = `${type}_${cleanId}`;
            addRequired(key, type, cleanId, Number(comp.qty_needed) || 0);
          }
        });
      }
    });

    const results = [];
    Object.values(required).forEach(req => {
      let masterItem = null;
      if (req.type === 'botol') masterItem = botolList.find(i => i.id === req.id);
      if (req.type === 'tutup') masterItem = tutupList.find(i => i.id === req.id);
      if (req.type === 'spray') masterItem = sprayList.find(i => i.id === req.id);
      if (req.type === 'fullset') masterItem = fullsetList.find(i => String(i.id) === String(req.id));
      if (req.type === 'bahan-cair') {
        masterItem = bahanCairList.find(i => i.id === req.id);
        if (masterItem) {
          // req.qty is already normalized to the final unit during calculation
        }
      }
      if (req.type === 'item-tambahan' || req.type === 'tambahan') {
        masterItem = itemTambahanList.find(i => String(i.id) === String(req.id));
      }
      if (masterItem && !masterItem.dari_client) {
        const currentStock = masterItem.stok || 0;
        const activeTickets = tickets.filter(t => String(t.material_id) === String(req.id) && t.material_type === req.type && t.status !== 'Sudah Datang');
        const ticketsToProcess = activeTickets.length > 0 ? activeTickets : [null];
        
        ticketsToProcess.forEach(ticket => {
          // ONLY include in "Item Sudah PO" if it IS being processed/shipped
          let isProcessed = ticket && ['Dalam Pengiriman', 'Menunggu Pembayaran', 'Sudah Dibayar'].includes(ticket.status);
          if (ticket && ticket.status !== 'Harus Dibeli' && !isProcessed && typeof requests !== 'undefined') {
            const relatedRequest = requests.find(req => {
              let match = false;
              if (req.batch_items) {
                let batchItems = [];
                try {
                  batchItems = typeof req.batch_items === 'string' ? JSON.parse(req.batch_items) : req.batch_items;
                } catch(e) {}
                if (Array.isArray(batchItems)) {
                  match = batchItems.some(bi => bi.ticket && String(bi.ticket.id) === String(ticket.id));
                }
              }
              if (!match && !req.batch_items && req.material_id) {
                 match = String(req.material_id) === String(ticket.material_id);
              }
              return match;
            });
            if (relatedRequest && relatedRequest.metode_pembayaran === 'Tempo' && relatedRequest.status !== 'Ditolak') {
              isProcessed = true;
            }
          }
          if (isProcessed) {
            let deficitQty = Number((req.qty - currentStock).toFixed(2));
            if (deficitQty > 0 || ticket) {
              const price = masterItem.harga_rupiah || masterItem.harga_beli || 0;
              results.push({
                ...masterItem,
                _reqQty: Number(req.qty.toFixed(2)),
                _deficitQty: ticket ? ticket.qty_to_buy : (deficitQty > 0 ? deficitQty : 0),
                _type: req.type,
                _ticket: ticket || null,
                _price: price,
                _brands: Array.from(req.brands || []),
                satuan: masterItem.satuan || 'pcs'
              });
            }
          }
        });
      }
    });

    // 2. Kumpulkan tiket manual yang SUDAH DIPROSES (status Dalam Permintaan / Menunggu Pembayaran / Sudah Dibayar / Dalam Pengiriman)
    const processedTicketIds = new Set(results.map(r => r._ticket?.id).filter(Boolean));
    const processedItemKeys = new Set(results.map(r => `${r._type}_${r.id}`));

    (tickets || []).forEach(ticket => {
      // Di halaman Item Sudah PO: HANYA tiket yang SUDAH DI-PO (bukan Harus Dibeli dan bukan Sudah Datang)
      if (!ticket || ticket.status === 'Harus Dibeli' || ticket.status === 'Sudah Datang' || processedTicketIds.has(ticket.id)) return;
      
      const type = ticket.material_type || 'bahan-cair';
      const matId = String(ticket.material_id);
      const itemKey = `${type}_${matId}`;
      if (processedItemKeys.has(itemKey)) return;

      let masterItem = null;
      if (type === 'botol') masterItem = botolList.find(i => String(i.id) === matId);
      else if (type === 'tutup') masterItem = tutupList.find(i => String(i.id) === matId);
      else if (type === 'spray') masterItem = sprayList.find(i => String(i.id) === matId);
      else if (type === 'fullset') masterItem = fullsetList.find(i => String(i.id) === matId);
      else if (type === 'bahan-cair') masterItem = bahanCairList.find(i => String(i.id) === matId);
      else if (type === 'item-tambahan' || type === 'tambahan') masterItem = itemTambahanList.find(i => String(i.id) === matId);

      if (masterItem) {
        masterItem._type = type;
        const price = masterItem.harga_rupiah || masterItem.harga_beli || 0;
        const buyQty = ticket.qty_to_buy || 0;
        if (buyQty > 0) {
          results.push({
            ...masterItem,
            _reqQty: 0,
            _deficitQty: buyQty,
            _type: type,
            _ticket: ticket,
            _price: price,
            _isManualInput: true,
            _brands: ['Input Manual'],
            satuan: masterItem.satuan || 'pcs',
            vendor: ticket.vendor || masterItem.vendor || masterItem.supplier || ''
          });
          processedItemKeys.add(itemKey);
        }
      }
    });

    return results
      .filter(item => {
        const key = getItemKey(item);
        if (ignoredKeys.has(key)) return false;
        // Strictly only show items that are needed by active POs or explicit manual input
        const isManual = item._isManualInput || item._ticket?.is_manual || item.is_manual;
        if ((item._reqQty || 0) <= 0 && !isManual) return false;
        return true;
      })
      .sort((a, b) => {
        if (!a._isEmptyItem && b._isEmptyItem) return -1;
        if (a._isEmptyItem && !b._isEmptyItem) return 1;
        return b._deficitQty - a._deficitQty;
      });
  }, [activePOs, clientPOs, botolList, tutupList, sprayList, bahanCairList, itemTambahanList, tickets, productionOrders, requests, ignoredKeys]);

  // ── GROUP DEFICITS BY FULLSET ──
  const { fullsetCards, individualDeficits } = useMemo(() => {
    const fullsetCards = [];
    const claimedIds = new Set(); // track items already claimed by a fullset

    fullsetList.forEach(fs => {
      if (fs.status === 'nonaktif') return;
      const componentIds = [];
      if (fs.botol_id) componentIds.push({ type: 'botol', id: fs.botol_id });
      if (fs.tutup_id) componentIds.push({ type: 'tutup', id: fs.tutup_id });
      if (fs.spray_id) componentIds.push({ type: 'spray', id: fs.spray_id });

      // Find which of this fullset's components are in deficit
      const matchedDeficits = [];
      componentIds.forEach(comp => {
        const match = deficits.find(d => d._type === comp.type && String(d.id) === String(comp.id) && !claimedIds.has(`${d._type}_${d.id}`));
        if (match) matchedDeficits.push(match);
      });

      if (matchedDeficits.length > 0) {
        matchedDeficits.forEach(d => claimedIds.add(`${d._type}_${d.id}`));
        fullsetCards.push({
          _isFullsetCard: true,
          fullset: fs,
          items: matchedDeficits,
          _allComponents: componentIds,
        });
      }
    });

    const individualDeficits = deficits.filter(d => !claimedIds.has(`${d._type}_${d.id}`));
    return { fullsetCards, individualDeficits };
  }, [deficits, fullsetList]);

  // ── GROUP INDIVIDUAL DEFICITS BY SUPPLIER ──
  const supplierGrouped = useMemo(() => {
    const groups = {};
    individualDeficits.forEach(item => {
      const itemVendor = (item.vendor || item.supplier || '').trim();
      // Find the supplier that handles this vendor
      let matchedSupplier = null;
      if (itemVendor) {
        matchedSupplier = suppliers.find(s => {
          let match = false;
          if (s.handled_vendors && Array.isArray(s.handled_vendors) && s.handled_vendors.length > 0) {
            match = s.handled_vendors.some(v => String(v || '').toLowerCase() === String(itemVendor || '').toLowerCase());
          }
          if (!match) {
            match = String(s.nama_sales || '').toLowerCase() === String(itemVendor || '').toLowerCase();
          }
          return match;
        });
      }
      const groupKey = matchedSupplier ? `supplier_${matchedSupplier.id}` : (itemVendor ? `vendor_${itemVendor}` : 'no_vendor');
      const groupLabel = matchedSupplier ? matchedSupplier.nama_sales : (itemVendor || 'Tanpa Vendor');
      if (!groups[groupKey]) {
        groups[groupKey] = {
          key: groupKey,
          label: groupLabel,
          supplier: matchedSupplier || null,
          vendorName: itemVendor,
          items: []
        };
      }
      groups[groupKey].items.push(item);
    });
    // Sort: groups with matched supplier first, then by item count descending
    const result = Object.values(groups).sort((a, b) => {
      if (a.supplier && !b.supplier) return -1;
      if (!a.supplier && b.supplier) return 1;
      if (a.key === 'no_vendor') return 1;
      if (b.key === 'no_vendor') return -1;
      return b.items.length - a.items.length;
    });
    
    return result;
  }, [individualDeficits, suppliers]);

  const filteredSupplierGrouped = useMemo(() => {
    let filtered = supplierGrouped;
    if (selectedSupplierFilter !== 'all') {
      filtered = filtered.filter(g => g.key === selectedSupplierFilter);
    }
    if (mainSearchQuery) {
      const q = mainSearchQuery.toLowerCase().trim();
      filtered = filtered.map(group => {
        const matchingItems = group.items.filter(item => {
          const nameMatch = (item.nama || item.name || '').toLowerCase().includes(q);
          const catMatch = (item.kategori || item._type || '').toLowerCase().includes(q);
          const vendorMatch = (item.vendor || item.supplier || group.label || group.vendorName || '').toLowerCase().includes(q);
          const codeMatch = (item.kode || '').toLowerCase().includes(q);
          const brandMatch = (item._brands || []).some(b => String(b).toLowerCase().includes(q)) || 
                             (item._brandNames || []).some(b => String(b).toLowerCase().includes(q));
          const poMatch = (item._poDates || []).some(p => String(p).toLowerCase().includes(q));
          return nameMatch || catMatch || vendorMatch || codeMatch || brandMatch || poMatch;
        });
        return { ...group, items: matchingItems };
      }).filter(group => group.items.length > 0);
    }
    return filtered;
  }, [supplierGrouped, selectedSupplierFilter, mainSearchQuery]);

  // ── Qty Input Helpers ──
  const getQtyForItem = (item) => {
    const key = getItemKey(item);
    const input = qtyInputs[key];
    if (input) return input;
    return { qty: item._deficitQty, confirmed: true };
  };

  const setQtyForItem = (item, qty) => {
    const key = getItemKey(item);
    setQtyInputs(prev => ({ ...prev, [key]: { qty: qty, confirmed: false } }));
  };

  const confirmQtyForItem = async (item) => {
    const key = getItemKey(item);
    const current = qtyInputs[key] || { qty: item._deficitQty, confirmed: false };
    const numericQty = Number(String(current.qty).replace(',', '.')) || 0;
    setQtyInputs(prev => ({ ...prev, [key]: { qty: numericQty, confirmed: true } }));
    
    // Auto-save to database
    if (item._ticket) {
      await updateTicketMut.mutateAsync({ id: item._ticket.id, data: { qty_to_buy: numericQty } });
      toast.success(`Jumlah ${item.nama} berhasil diupdate jadi ${numericQty}`);
    } else {
      await createTicketMut.mutateAsync({
        material_id: item.id,
        material_type: item._type,
        material_name: item.nama,
        qty_to_buy: numericQty,
        status: 'Harus Dibeli'
      });
      toast.success(`Jumlah ${item.nama} berhasil diupdate jadi ${numericQty}`);
    }
  };

  const editQtyForItem = (item) => {
    const key = getItemKey(item);
    const current = qtyInputs[key] || { qty: item._deficitQty, confirmed: true };
    setQtyInputs(prev => ({ ...prev, [key]: { ...current, confirmed: false } }));
  };

  // ── Batch Selection Helpers ──
  const toggleSelection = (item) => {
    const key = getItemKey(item);
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
        if (next.size === 0) setIsSelectionMode(false);
      } else {
        next.add(key);
        setIsSelectionMode(true);
      }
      return next;
    });
  };

  const selectAll = () => {
    if (selectedIds.size === deficits.length) {
      setSelectedIds(new Set());
      setIsSelectionMode(false);
    } else {
      setSelectedIds(new Set(deficits.map(getItemKey)));
      setIsSelectionMode(true);
    }
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
    setIsSelectionMode(false);
  };

  const handleIgnoreItem = (item) => {
    const key = getItemKey(item);
    if (item._ticket?.id) {
      deleteTicketMut.mutate(item._ticket.id);
      if (item._ticket._merged_ids) {
        item._ticket._merged_ids.forEach(id => deleteTicketMut.mutate(id));
      }
    }
    const relatedTickets = tickets.filter(t => String(t.material_id) === String(item.id) && t.material_type === item._type);
    relatedTickets.forEach(t => deleteTicketMut.mutate(t.id));

    setIgnoredKeys(prev => {
      const updated = new Set(prev);
      updated.add(key);
      try { localStorage.setItem('sde_purchasing_po_ignored_items', JSON.stringify([...updated])); } catch (e) {}
      return updated;
    });
    toast.success(`"${item.nama || item.name}" berhasil dihapus dari daftar.`);
  };

  const handleBatchIgnoreOrDelete = () => {
    const selectedItems = deficits.filter(d => selectedIds.has(getItemKey(d)));
    if (selectedItems.length === 0) return;

    selectedItems.forEach(item => {
      if (item._ticket?.id) {
        deleteTicketMut.mutate(item._ticket.id);
        if (item._ticket._merged_ids) {
          item._ticket._merged_ids.forEach(id => deleteTicketMut.mutate(id));
        }
      }
      const relatedTickets = tickets.filter(t => String(t.material_id) === String(item.id) && t.material_type === item._type);
      relatedTickets.forEach(t => deleteTicketMut.mutate(t.id));
    });

    setIgnoredKeys(prev => {
      const updated = new Set(prev);
      selectedItems.forEach(item => updated.add(getItemKey(item)));
      try { localStorage.setItem('sde_purchasing_po_ignored_items', JSON.stringify([...updated])); } catch (e) {}
      return updated;
    });

    clearSelection();
    toast.success(`${selectedItems.length} item berhasil dihapus dari daftar.`);
  };

  // ── Batch Fill Qty ──
  const handleBatchFillQty = () => {
    const val = parseFloat(batchFillQty);
    if (isNaN(val) || val <= 0) return;
    const newInputs = { ...qtyInputs };
    deficits.forEach(item => {
      const key = getItemKey(item);
      if (selectedIds.has(key)) {
        newInputs[key] = { qty: val, confirmed: true };
      }
    });
    setQtyInputs(newInputs);
    setBatchFillQty('');
    toast.success(`Qty ${val} berhasil diisi ke ${selectedIds.size} item terpilih`);
  };

  // ── Update Stock helper ──
  const updateStock = async (item, addedQty) => {
    const numericAdded = parseFloat(String(addedQty).replace(',', '.')) || 0;
    if (numericAdded <= 0) return;

    if (item._type === 'botol') {
      const current = botolList.find(b => String(b.id) === String(item.id))?.stok || item.stok || 0;
      await base44.entities.Botol.update(item.id, { stok: Number((current + numericAdded).toFixed(2)) });
      queryClient.invalidateQueries({ queryKey: ['botol'] });
    } else if (item._type === 'tutup') {
      const current = tutupList.find(t => String(t.id) === String(item.id))?.stok || item.stok || 0;
      await base44.entities.Tutup.update(item.id, { stok: Number((current + numericAdded).toFixed(2)) });
      queryClient.invalidateQueries({ queryKey: ['tutup'] });
    } else if (item._type === 'spray') {
      const current = sprayList.find(s => String(s.id) === String(item.id))?.stok || item.stok || 0;
      await base44.entities.Spray.update(item.id, { stok: Number((current + numericAdded).toFixed(2)) });
      queryClient.invalidateQueries({ queryKey: ['spray'] });
    } else if (item._type === 'item-tambahan') {
      const current = itemTambahanList.find(i => String(i.id) === String(item.id))?.stok || item.stok || 0;
      await base44.entities.ItemTambahan.update(item.id, { stok: Number((current + numericAdded).toFixed(2)) });
      queryClient.invalidateQueries({ queryKey: ['item-tambahan'] });
    } else if (item._type === 'bahan-cair') {
      const current = bahanCairList.find(b => String(b.id) === String(item.id))?.stok || item.stok || 0;
      await base44.entities.BahanCair.update(item.id, { stok: Number((current + numericAdded).toFixed(2)) });
      queryClient.invalidateQueries({ queryKey: ['bahan-cair'] });
    } else if (item._type === 'fullset') {
      // Update Fullset itself
      const current = fullsetList.find(f => String(f.id) === String(item.id))?.stok || item.stok || 0;
      await base44.entities.Fullset.update(item.id, { stok: Number((current + numericAdded).toFixed(2)) });

      // Also update constituent components if defined
      if (item.botol_id) {
        const bStok = (botolList.find(b => String(b.id) === String(item.botol_id))?.stok || 0) + numericAdded;
        await base44.entities.Botol.update(item.botol_id, { stok: Number(bStok.toFixed(2)) });
      }
      if (item.tutup_id) {
        const tStok = (tutupList.find(t => String(t.id) === String(item.tutup_id))?.stok || 0) + numericAdded;
        await base44.entities.Tutup.update(item.tutup_id, { stok: Number(tStok.toFixed(2)) });
      }
      if (item.spray_id) {
        const sStok = (sprayList.find(s => String(s.id) === String(item.spray_id))?.stok || 0) + numericAdded;
        await base44.entities.Spray.update(item.spray_id, { stok: Number(sStok.toFixed(2)) });
      }
      queryClient.invalidateQueries({ queryKey: ['fullset'] });
      queryClient.invalidateQueries({ queryKey: ['botol'] });
      queryClient.invalidateQueries({ queryKey: ['tutup'] });
      queryClient.invalidateQueries({ queryKey: ['spray'] });
    }
  };

  // ── Open Pengajuan Modal ──
  const handleOpenPengajuan = (items, supplierData = null) => {
    setPengajuanItem(items); 
    // Auto-fill from supplier data if provided
    if (supplierData) {
      setPengajuanNamaSales(supplierData.nama_sales || '');
      setPengajuanNamaPerusahaan(supplierData.nama_perusahaan || '');
      setPengajuanNPWP(supplierData.npwp || '');
      setPengajuanBank(supplierData.bank || '');
      setPengajuanNoRek(supplierData.no_rekening || '');
      setPengajuanNamaRek(supplierData.atas_nama || '');
      setPengajuanMetode(supplierData.metode_pembayaran || 'Cash');
      setPengajuanOngkirMode(supplierData.ongkir_mode || 'transfer');
      setPengajuanSupplierPhone(supplierData.phone || '');
      setPengajuanSupplierAddress(supplierData.alamat || '');
      setSupplierSearch(supplierData.nama_sales || supplierData.nama_perusahaan || '');
    } else {
      setPengajuanNamaSales('');
      setPengajuanNamaPerusahaan('');
      setPengajuanNPWP('');
      setPengajuanBank('BCA');
      setPengajuanNoRek('');
      setPengajuanNamaRek('');
      setPengajuanMetode('Cash');
      setPengajuanOngkirMode('transfer');
      setPengajuanSupplierPhone('');
      setPengajuanSupplierAddress('');
      setSupplierSearch('');
    }
    setPengajuanTempo('');
    setPengajuanOngkir('');
    setPengajuanCatatan('');
    
    // Set default buyer info
    setPengajuanPemesanNama(companySettings?.company_name || 'PT SATALA DERMATECH ESSENTIAL');
    setPengajuanPemesanNPWP(companySettings?.npwp || '');
    setPengajuanPemesanAlamat(companySettings?.address || '');
    setPengajuanPemesanPIC(member?.full_name || 'Purchasing Staff');
    setPengajuanPemesanTelp(companySettings?.phone || '');

    setShowSupplierDropdown(false);
    setShowPengajuanModal(true);
  };

  // ── Batch Status Update ──
  const handleBatchStatusSubmit = async () => {
    if (!batchTargetStatus) return;
    
    const itemsToProcess = Array.from(selectedIds).map(id => {
      const parts = String(id).split('_');
      const type = parts[0];
      const realId = parts.slice(1).join('_');
      return deficits.find(i => String(i.id) === realId && i._type === type);
    }).filter(Boolean);

    if (batchTargetStatus === 'Dalam Permintaan') {
      setShowBatchStatusModal(false);
      handleOpenPengajuan(itemsToProcess);
      return;
    }

    // Batch "Sudah Datang" → redirect ke modal ekspedisi
    if (batchTargetStatus === 'Sudah Datang') {
      setShowBatchStatusModal(false);
      setEkspedisiItem(itemsToProcess);
      setEkspedisiNama('');
      setShowEkspedisiModal(true);
      return;
    }

    setIsBatchProcessing(true);
    const selectedItems = deficits.filter(d => selectedIds.has(getItemKey(d)));
    const itemsProcessed = [];

    try {
      for (const item of selectedItems) {
        const qtyData = getQtyForItem(item);
        const buyQty = qtyData.qty || item._deficitQty;

        if (batchTargetStatus === 'Sudah Datang') {
          // Update stock
          await updateStock(item, buyQty);
        }

        // Update/Create ticket
        if (item._ticket) {
          await updateTicketMut.mutateAsync({ id: item._ticket.id, data: { status: batchTargetStatus, qty_to_buy: buyQty } });
        } else {
          await createTicketMut.mutateAsync({
            material_id: item.id,
            material_type: item._type,
            material_name: item.nama,
            qty_to_buy: buyQty,
            status: batchTargetStatus
          });
        }

        itemsProcessed.push({
          inventory_item_id: item.id,
          nama_barang: item.nama,
          kode: item.kode || '',
          kategori: item.kategori || item._type,
          satuan: item.satuan || 'pcs',
          jumlah: buyQty,
          vendor: item._ticket?.vendor || item.vendor || item.supplier || ''
        });
      }

      // Note: "Sudah Datang" batch sekarang di-handle lewat modal ekspedisi (handleConfirmArrival)

      // Show success popup
      setShowSuccessPopup({
        status: batchTargetStatus,
        items: itemsProcessed,
        count: itemsProcessed.length
      });

      clearSelection();
      setShowBatchStatusModal(false);
      setBatchTargetStatus('');
    } catch (err) {
      toast.error('Gagal update: ' + err.message);
    } finally {
      setIsBatchProcessing(false);
    }
  };

  // ── Single item status change ──
  const handleStatusChange = async (item, newStatus) => {
    if (newStatus === 'Dalam Permintaan') {
      handleOpenPengajuan([item]);
      return;
    }

    const qtyData = getQtyForItem(item);
    const buyQty = qtyData.qty !== undefined ? Number(String(qtyData.qty).replace(',', '.')) : item._deficitQty;

    // Redirect "Sudah Datang" ke modal ekspedisi dulu
    if (newStatus === 'Sudah Datang') {
      setEkspedisiItem([item]);
      setEkspedisiNama('');
      setShowEkspedisiModal(true);
      return;
    }

    if (item._ticket) {
      await updateTicketMut.mutateAsync({ id: item._ticket.id, data: { status: newStatus, qty_to_buy: buyQty } });
    } else {
      await createTicketMut.mutateAsync({ material_id: item.id, material_type: item._type, material_name: item.nama, qty_to_buy: buyQty, status: newStatus });
    }
    toast.success(`Status ${item.nama} → ${newStatus}`);
  };

  // ── Confirm Arrival (dari Ekspedisi Modal) ──
  const handleConfirmArrival = async () => {
    if (!ekspedisiItem || ekspedisiItem.length === 0) return;
    if (!ekspedisiNama.trim()) {
      toast.error('Mohon isi nama ekspedisi terlebih dahulu!');
      return;
    }

    setIsProcessingArrival(true);
    const itemsProcessed = [];

    try {
      for (const item of ekspedisiItem) {
        const qtyData = getQtyForItem(item);
        const buyQty = qtyData.qty || item._deficitQty;

        // 1. Update stok
        await updateStock(item, buyQty);

        // 2. Update/Create ticket
        if (item._ticket) {
          await updateTicketMut.mutateAsync({ id: item._ticket.id, data: { status: 'Sudah Datang', qty_to_buy: buyQty } });
          
          // 2.5 Find and update related PurchasingRequest if it is 'Dalam Pengiriman'
          const relatedReq = requests.find(r => 
            r.status === 'Dalam Pengiriman' && 
            isRequestMatchingItem(r, item.id, item._type)
          );
          if (relatedReq) {
            await base44.entities.PurchasingRequest.update(relatedReq.id, { status: 'Barang Datang' });
          }
        } else {
          await createTicketMut.mutateAsync({ material_id: item.id, material_type: item._type, material_name: item.nama, qty_to_buy: buyQty, status: 'Sudah Datang' });
        }

        itemsProcessed.push({
          inventory_item_id: item.id,
          nama_barang: item.nama,
          kode: item.kode || '',
          kategori: item.kategori || item._type,
          satuan: item.satuan || 'pcs',
          jumlah: buyQty,
          vendor: item._ticket?.vendor || item.vendor || item.supplier || ''
        });
      }

      // 3. Catat Barang Masuk dengan format referensi SDE-BELI-{kategori}-{bulan}-{tanggal}
      if (itemsProcessed.length > 0) {
        const firstItem = ekspedisiItem[0];
        const refNumber = generateRefNumber(firstItem);
        // Cari ongkir dari PurchasingRequest terkait (jika ada)
        const relatedTicket = firstItem._ticket;
        
        try {
          await base44.entities.BarangMasuk.create({
            tanggal: new Date().toISOString().split('T')[0],
            nomor_referensi: refNumber,
            supplier: itemsProcessed[0].vendor || 'Supplier',
            ekspedisi: ekspedisiNama.trim(),
            ongkir: relatedTicket?.ongkir || 0,
            ongkir_dibayar_oleh: 'Perusahaan',
            catatan: `Barang masuk via Purchasing — Ekspedisi: ${ekspedisiNama.trim()}`,
            pic: member?.full_name || member?.email || 'Purchasing Staff',
            input_by: member?.email || 'System',
            items: itemsProcessed
          });
        } catch (err) {
          console.error('Failed to create BarangMasuk', err);
        }
      }

      // 4. Success popup
      setShowSuccessPopup({
        status: 'Sudah Datang',
        items: itemsProcessed,
        count: itemsProcessed.length
      });

      setShowEkspedisiModal(false);
      setEkspedisiItem(null);
      clearSelection();
    } catch (err) {
      toast.error('Gagal memproses kedatangan: ' + err.message);
    } finally {
      setIsProcessingArrival(false);
    }
  };

  // ── Submit Pengajuan ──
  const handleSubmitPengajuan = async () => {
    if (!pengajuanItem || pengajuanItem.length === 0) return;
    if (!pengajuanNoRek.trim() || !pengajuanNamaRek.trim() || !pengajuanNamaSales.trim()) {
      toast.error('Mohon lengkapi nama sales, nomor rekening, dan nama pemilik rekening!');
      return;
    }
    if (pengajuanMetode === 'Tempo' && !pengajuanTempo) {
      toast.error('Tanggal jatuh tempo wajib diisi untuk metode Tempo!');
      return;
    }

    setIsSubmittingPengajuan(true);
    try {
      let totalBarang = 0;
      let totalPpn = 0;
      const batchItemsData = [];

    // Calculate total and prepare ticket data
    for (const item of pengajuanItem) {
      const qtyData = getQtyForItem(item);
      const buyQty = qtyData.qty || item._deficitQty;
      
      const isEur = item.mata_uang === 'EUR';
      const isUsd = item.mata_uang === 'USD';
      const isValas = isUsd || isEur;
      const currentRate = isEur ? (kursEuro || 17000) : (kurs || 16000);
      const valasUnit = isEur ? (parseFloat(item.harga_euro) || null) : (isUsd ? (parseFloat(item.harga_dollar) || null) : null);
      const unitPriceRupiah = (isValas && valasUnit != null && valasUnit > 0)
        ? (valasUnit * currentRate)
        : parseFloat(item.harga_rupiah || item.harga_beli || 0);

      let baseTotal = 0;
      let ppnAmt = 0;

      if (item.ppn_type === 'exclude' && item.harga_sebelum_ppn) {
        const basePrice = (isValas && valasUnit != null && valasUnit > 0) ? (valasUnit * currentRate) : parseFloat(item.harga_sebelum_ppn);
        baseTotal = buyQty * basePrice;
        const pct = item.ppn_percentage ? parseFloat(item.ppn_percentage) : 11;
        ppnAmt = baseTotal * (pct / 100);
      } else if (item.ppn_type === 'include') {
        baseTotal = (buyQty * unitPriceRupiah) / 1.11;
        ppnAmt = (buyQty * unitPriceRupiah) - baseTotal;
      } else {
        baseTotal = buyQty * unitPriceRupiah;
        if (item.ppn_type === 'exclude') {
          const pct = item.ppn_percentage ? parseFloat(item.ppn_percentage) : 11;
          ppnAmt = baseTotal * (pct / 100);
        }
      }
      
      totalBarang += baseTotal;
      totalPpn += ppnAmt;
      batchItemsData.push({
        id: item.id,
        type: item._type,
        name: item.nama,
        qty: buyQty,
        ticket: item._ticket,
        harga_rupiah: unitPriceRupiah,
        mata_uang: isEur ? 'EUR' : (isUsd ? 'USD' : 'IDR'),
        harga_dollar: isUsd ? valasUnit : null,
        harga_euro: isEur ? valasUnit : null,
        satuan: item.satuan || 'pcs',
        ppn_type: item.ppn_type || 'include',
        ppn_percentage: item.ppn_percentage || 11,
        harga_sebelum_ppn: item.ppn_type === 'exclude' ? ((isValas && valasUnit) ? valasUnit * currentRate : (item.harga_sebelum_ppn || unitPriceRupiah)) : unitPriceRupiah
      });
    }

      const ongkirVal = pengajuanOngkirMode === 'free_ongkir' ? 0 : (parseFloat(pengajuanOngkir) || 0);
      const totalTransfer = (pengajuanOngkirMode === 'transfer' ? totalBarang + totalPpn + ongkirVal : totalBarang + totalPpn);
      const isBatch = pengajuanItem.length > 1;

      // Determine PPN info for display
      const hasExcludePpn = pengajuanItem.some(it => it.ppn_type === 'exclude');
      const ppnPctDisplay = hasExcludePpn ? (pengajuanItem.find(it => it.ppn_type === 'exclude')?.ppn_percentage || 11) : 0;

      const generatedPoNumber = generatePONumber({
        buyerName: pengajuanPemesanNama,
        salesName: pengajuanNamaSales,
        existingRequests: requests
      });

      // 1. Create PurchasingRequest record
      await base44.entities.PurchasingRequest.create({
        nama_item: isBatch ? `Batch Order: ${pengajuanItem.length} Items (Multivendor)` : pengajuanItem[0].nama,
        kategori: isBatch ? 'Batch Request' : (pengajuanItem[0]._type === 'bahan-cair' ? 'Bahan Cair' : pengajuanItem[0]._type),
        stok_gudang: isBatch ? 0 : (pengajuanItem[0].stok || 0),
        kebutuhan: isBatch ? 0 : (pengajuanItem[0]._reqQty || 0),
        qty_beli: isBatch ? pengajuanItem.length : batchItemsData[0].qty,
        harga_unit: isBatch ? 0 : batchItemsData[0].harga_rupiah,
        harga_dollar: isBatch ? null : batchItemsData[0].harga_dollar,
        harga_euro: isBatch ? null : batchItemsData[0].harga_euro,
        mata_uang: isBatch ? null : batchItemsData[0].mata_uang,
        satuan: isBatch ? 'Item' : (pengajuanItem[0].satuan || 'pcs'),
        total_biaya: totalTransfer,
        total_barang: totalBarang,
        ongkir: ongkirVal,
        ongkir_mode: pengajuanOngkirMode,
        ppn_status: hasExcludePpn ? 'exclude' : 'include',
        ppn_percentage: hasExcludePpn ? ppnPctDisplay : null,
        ppn_nominal: totalPpn,
        bank_tujuan: pengajuanBank,
        nomor_rekening: pengajuanNoRek.trim(),
        atas_nama: pengajuanNamaRek.trim(),
        catatan: pengajuanCatatan.trim(),
        nama_sales: pengajuanNamaSales.trim(),
        nama_perusahaan: pengajuanNamaPerusahaan.trim(),
        npwp: pengajuanNPWP.trim(),
        supplier_phone: pengajuanSupplierPhone.trim(),
        supplier_address: pengajuanSupplierAddress.trim(),
        po_number: generatedPoNumber,
        kurs_usd: pengajuanItem.some(it => it.mata_uang === 'USD' || it.harga_dollar) ? kurs : null,
        kurs_eur: pengajuanItem.some(it => it.mata_uang === 'EUR' || it.harga_euro) ? kursEuro : null,
        metode_pembayaran: pengajuanMetode,
        tanggal_jatuh_tempo: pengajuanMetode === 'Tempo' ? pengajuanTempo : null,
        material_id: isBatch ? null : pengajuanItem[0].id,
        material_type: isBatch ? null : pengajuanItem[0]._type,
        batch_items: JSON.stringify(batchItemsData),
        status: pengajuanMetode === 'Tempo' ? 'Dalam Pengiriman' : 'Menunggu Pembayaran',
        status_pembayaran_tempo: pengajuanMetode === 'Tempo' ? 'Menunggu Jatuh Tempo' : null,
        pic: member?.full_name || member?.email || 'Purchasing Staff',
        buyer_info: JSON.stringify({
          company_name: pengajuanPemesanNama,
          npwp: pengajuanPemesanNPWP,
          address: pengajuanPemesanAlamat,
          pic: pengajuanPemesanPIC,
          phone: pengajuanPemesanTelp
        }),
        syarat_po: JSON.stringify(pengajuanSyaratList),
        created_date: new Date().toISOString()
      });

      // 2. Update/Create purchasing tickets in parallel
      const nextStatus = pengajuanMetode === 'Tempo' ? 'Dalam Pengiriman' : 'Dalam Permintaan';
      const ticketPromises = batchItemsData.flatMap((b) => {
        if (b.ticket) {
          const ops = [
            base44.entities.PurchasingTicket.update(b.ticket.id, { status: nextStatus, qty_to_buy: b.qty })
          ];
          if (b.ticket._merged_ids && b.ticket._merged_ids.length > 0) {
            b.ticket._merged_ids.forEach((extraId) => {
              ops.push(base44.entities.PurchasingTicket.hardDelete(extraId));
            });
          }
          return ops;
        } else {
          return [
            base44.entities.PurchasingTicket.create({
              material_id: b.id,
              material_type: b.type,
              material_name: b.name,
              qty_to_buy: b.qty,
              status: nextStatus
            })
          ];
        }
      });

      await Promise.all(ticketPromises);

      setShowPrintPopup({
        po_number: generatedPoNumber,
        syarat_po: pengajuanSyaratList,
        created_date: new Date().toISOString(),
        nama_sales: pengajuanNamaSales.trim(),
        supplier_phone: pengajuanSupplierPhone.trim(),
        supplier_address: pengajuanSupplierAddress.trim(),
        metode_pembayaran: pengajuanMetode,
        tanggal_jatuh_tempo: pengajuanMetode === 'Tempo' ? pengajuanTempo : null,
        bank_tujuan: pengajuanBank,
        nomor_rekening: pengajuanNoRek.trim(),
        atas_nama: pengajuanNamaRek.trim(),
        pic: member?.full_name || member?.email || 'Purchasing Staff',
        batch_items: batchItemsData,
        ongkir: ongkirVal,
        ongkir_mode: pengajuanOngkirMode,
        catatan: pengajuanCatatan.trim(),
        kurs_usd: pengajuanItem.some(it => it.mata_uang === 'USD') ? kurs : null,
        kurs_eur: pengajuanItem.some(it => it.mata_uang === 'EUR') ? kursEuro : null,
        companySettings,
        buyer_info: {
          company_name: pengajuanPemesanNama,
          npwp: pengajuanPemesanNPWP,
          address: pengajuanPemesanAlamat,
          pic: pengajuanPemesanPIC,
          phone: pengajuanPemesanTelp
        }
      });
      
      setShowPengajuanModal(false);
      setPengajuanItem(null);
      clearSelection();
      queryClient.invalidateQueries({ queryKey: ['purchasing-tickets'] });
      queryClient.invalidateQueries({ queryKey: ['purchasing-requests'] });
    } catch (err) {
      toast.error('Gagal membuat pengajuan: ' + err.message);
    } finally {
      setIsSubmittingPengajuan(false);
    }
  };

  // ── Asa Insights ──
  const asaInsights = useMemo(() => {
    const insights = [];
    
    const shippedItems = deficits.filter(d => d._ticket?.status === 'Dalam Pengiriman');
    
    // Check for items shipped > 3 days ago if updated_at is available
    const threeDaysAgo = new Date();
    threeDaysAgo.setDate(threeDaysAgo.getDate() - 3);

    const delayedItems = shippedItems.filter(d => {
      const lastUpdated = d._ticket?.updated_date || d._ticket?.updated_at || d._ticket?.created_date;
      if (lastUpdated) {
        return new Date(lastUpdated) < threeDaysAgo;
      }
      return false; 
    });

    if (delayedItems.length > 0) {
      insights.push({
        id: 'delayed-shipping',
        type: 'alert',
        text: `🚨 **Peringatan Keterlambatan:** Ada **${delayedItems.length} item material** yang berstatus "Dalam Pengiriman" dan belum update. Segera *follow-up* ke pihak ekspedisi/supplier, atau klik tombol **"Sudah Sampai"** jika barang sebenarnya sudah tiba di gudang.`
      });
    }

    const unpaidItems = deficits.filter(d => d._ticket?.status === 'Menunggu Pembayaran');
    if (unpaidItems.length > 0) {
      insights.push({
        id: 'unpaid-items',
        type: 'warning',
        text: `Terdapat **${unpaidItems.length} item** yang masih menunggu pembayaran Finance. Pastikan untuk mengingatkan mereka jika dirasa mendesak agar PO bisa segera diproses supplier.`
      });
    }

    const paidItems = deficits.filter(d => d._ticket?.status === 'Sudah Dibayar');
    if (paidItems.length > 0) {
      insights.push({
        id: 'paid-items',
        type: 'info',
        text: `💡 **Info**: Ada **${paidItems.length} item** yang sudah lunas dibayar. Pantau terus statusnya dan segera update ke "*Dalam Pengiriman*" saat resi/surat jalan sudah diberikan oleh supplier.`
      });
    }

    return insights;
  }, [deficits]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'Harus Dibeli': return 'bg-rose-100 text-rose-700 border-rose-200';
      case 'Dalam Permintaan': return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'Sudah Dibayar': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'Dalam Pengiriman': return 'bg-indigo-100 text-indigo-700 border-indigo-200';
      case 'Sudah Datang': return 'bg-emerald-100 text-emerald-700 border-emerald-200';
      case 'Sudah Selesai': return 'bg-teal-100 text-teal-700 border-teal-200';
      default: return 'bg-slate-100 text-slate-700 border-slate-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Sudah Datang': return <PackageCheck className="w-4 h-4" />;
      case 'Dalam Pengiriman': return <Truck className="w-4 h-4" />;
      case 'Sudah Dibayar': return <CreditCard className="w-4 h-4" />;
      case 'Dalam Permintaan': return <Wallet className="w-4 h-4" />;
      case 'Sudah Selesai': return <CheckCircle className="w-4 h-4" />;
      default: return null;
    }
  };

  const fmt = (n) => n != null ? new Intl.NumberFormat('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n) : '—';
  const fmtDecimal = (n, unit = '') => {
    if (n == null || isNaN(n)) return '0';
    const num = Number(n);
    const u = String(unit).toLowerCase().trim();
    const isDiscrete = !unit || ['pcs', 'buah', 'botol', 'tutup', 'spray', 'fullset', 'set', 'pack', 'box', 'roll', 'pasang', 'lembar', 'unit'].includes(u);
    if (isDiscrete || Number.isInteger(num)) {
      return new Intl.NumberFormat('id-ID', { maximumFractionDigits: 0 }).format(num);
    }
    return new Intl.NumberFormat('id-ID', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(num);
  };

  const calculateLeadTime = (startInput, endInput) => {
    if (!startInput) return null;
    const start = new Date(startInput);
    const end = endInput ? new Date(endInput) : new Date();
    if (isNaN(start.getTime()) || isNaN(end.getTime())) return null;

    const diffMs = Math.max(0, end - start);
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const days = Math.floor(diffHours / 24);
    const hours = diffHours % 24;

    if (days > 0) {
      return hours > 0 ? `${days} Hari ${hours} Jam` : `${days} Hari`;
    }
    return `${Math.max(1, hours)} Jam`;
  };



  const isLoading = loadingPOs || loadingDbBahan || loadingTickets || loadingProdOrders;

  // ── Render Individual Deficit Card ──
  const renderDeficitCard = (item, idx, groupSupplier = null) => {
    const key = getItemKey(item);
    const status = item._ticket?.status || 'Harus Dibeli';
    const qtyData = getQtyForItem(item);
    
    // Find matching request
    const matchingReq = requests.find(r => isRequestMatchingItem(r, item.id, item._type));
    let paymentBadge = null;
    if (matchingReq && status !== 'Harus Dibeli') {
      if (matchingReq.metode_pembayaran === 'Tempo') {
        paymentBadge = <Badge className="text-[9px] bg-orange-100 text-orange-700 border-orange-200 shadow-none">Tempo</Badge>;
      } else {
        if (matchingReq.status === 'Sudah Dibayar') {
          paymentBadge = <Badge className="text-[9px] bg-emerald-100 text-emerald-700 border-emerald-200 shadow-none">Sudah Dibayar</Badge>;
        } else {
          paymentBadge = <Badge className="text-[9px] bg-rose-100 text-rose-700 border-rose-200 shadow-none">Belum Dibayar</Badge>;
        }
      }
    }
    const displayQty = qtyData.qty;
    const numericDisplayQty = Number(String(displayQty).replace(',', '.')) || 0;

    // Check if there is a snapshot in matchingReq.batch_items
    let poBatchItem = null;
    if (matchingReq?.batch_items) {
      try {
        const parsed = typeof matchingReq.batch_items === 'string' ? JSON.parse(matchingReq.batch_items) : matchingReq.batch_items;
        if (Array.isArray(parsed)) {
          poBatchItem = parsed.find(b => (b.ticket && String(b.ticket.id) === String(item._ticket?.id)) || String(b.id) === String(item.id) || b.name === item.nama);
        }
      } catch(e) {}
    }

    const rawMataUang = poBatchItem?.mata_uang || matchingReq?.mata_uang || item.mata_uang;
    const isEur = rawMataUang === 'EUR';
    const isUsd = rawMataUang === 'USD';
    const isValas = isEur || isUsd;
    const valasSymbol = isEur ? '€' : '$';
    
    // Rate when PO was raised (frozen rate)
    const frozenRate = isEur 
      ? (matchingReq?.kurs_eur || kursEuro || 17000) 
      : (matchingReq?.kurs_usd || kurs || 16000);

    const valasUnit = isEur
      ? (poBatchItem?.harga_euro ?? matchingReq?.harga_euro ?? item.harga_euro ?? null)
      : (isUsd ? (poBatchItem?.harga_dollar ?? matchingReq?.harga_dollar ?? item.harga_dollar ?? null) : null);

    const unitPriceRupiah = (isValas && valasUnit != null && parseFloat(valasUnit) > 0) 
      ? (parseFloat(valasUnit) * frozenRate) 
      : (parseFloat(poBatchItem?.harga_rupiah || matchingReq?.harga_unit || item.harga_rupiah || item.harga_beli || 0));

    const totalCost = numericDisplayQty * unitPriceRupiah;
    const totalValas = (isValas && valasUnit != null) ? (numericDisplayQty * parseFloat(valasUnit)) : null;
    const isPpnExclude = (poBatchItem?.ppn_type === 'exclude') || (item.ppn_type === 'exclude') || (matchingReq?.ppn_status === 'exclude');

    const isSelected = selectedIds.has(key);
    const isConfirmed = qtyData.confirmed;

    return (
      <Card 
        key={`${key}-${idx}`} 
        onClick={() => { if (isSelectionMode) toggleSelection(item); }}
        className={`border overflow-hidden flex flex-col transition-all ${isSelectionMode ? 'cursor-pointer hover:ring-2 hover:ring-[#8C6208]/30' : ''} ${
          isSelected 
            ? 'border-[#8C6208] shadow-md ring-2 ring-[#8C6208]/20' 
            : 'border-slate-200/80 hover:border-[#8C6208]/40 hover:shadow-md'
        } bg-white`}
      >
        <CardContent className="p-0 flex flex-col h-full pointer-events-auto">
          {/* Top Status Banner + Checkbox */}
          <div className={`px-4 py-2 flex justify-between items-center border-b border-slate-100 ${status === 'Harus Dibeli' ? 'bg-rose-50/50' : 'bg-slate-50/50'}`}>
            <div className="flex items-center gap-2" onClick={(e) => { if (isSelectionMode) e.stopPropagation(); }}>
              <Checkbox
                checked={isSelected}
                onCheckedChange={() => toggleSelection(item)}
                className="border-slate-300 data-[state=checked]:bg-[#8C6208] data-[state=checked]:border-[#8C6208] pointer-events-auto"
              />
              <span className="text-[10px] font-black text-slate-500 uppercase tracking-wider">Status:</span>
            </div>
            <div className="flex items-center gap-2" onClick={(e) => { if (isSelectionMode) e.stopPropagation(); }}>
              {item._ticket && (item._ticket.updated_date || item._ticket.updated_at || item._ticket.created_date || item._ticket.created_at) && (
                <span className="text-[9px] text-slate-400 font-bold tracking-wide uppercase px-2 py-0.5 bg-white border border-slate-200 rounded hidden sm:block shadow-sm">
                  {new Date(item._ticket.updated_date || item._ticket.updated_at || item._ticket.created_date || item._ticket.created_at).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' })}
                </span>
              )}
              <div className="relative">
                <select 
                  value={status}
                  onChange={(e) => handleStatusChange(item, e.target.value)}
                  disabled={isSelectionMode}
                  className={`appearance-none cursor-pointer outline-none text-[10px] font-black uppercase tracking-wider px-3 py-1 pr-7 rounded-full border shadow-sm transition-colors ${getStatusColor(status)} ${isSelectionMode ? 'opacity-50 pointer-events-none' : ''}`}
                >
                  {STATUS_LIST.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                <ChevronDown className="w-3 h-3 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none opacity-50" />
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleIgnoreItem(item);
                }}
                title="Hapus / Batalkan item dari daftar"
                className="p-1 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          <div className="p-4 flex-1">
            {/* Name & Type */}
            <div 
              className={`flex gap-3 mb-3 ${isSelectionMode ? '' : 'cursor-pointer'}`} 
              onClick={(e) => {
                if (!isSelectionMode) navigate(`/office/purchasing/stok-detail?id=${item.id}&tipe=${item._type}`);
              }}
            >
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center border shrink-0 ${item._isEmptyItem ? 'bg-amber-50 border-amber-200 text-amber-500' : 'bg-slate-50 border-slate-100 text-slate-400'}`}>
                {item._isEmptyItem ? <Bot className="w-6 h-6" /> : <Package2 className="w-6 h-6" />}
              </div>
              <div className="min-w-0 flex-1">
                <h4 className="font-bold text-slate-800 text-base truncate">{item.nama}</h4>
                <div className="flex flex-wrap gap-1 mt-1">
                  <Badge variant="outline" className="text-[9px] uppercase bg-slate-50">{item._type === 'bahan-cair' ? 'BAHAN CAIR' : item._type}</Badge>
                  {item._type === 'botol' && item.ukuran_label_ml && <Badge variant="outline" className="text-[9px] bg-slate-50">{item.ukuran_label_ml}ml</Badge>}
                  {item._type === 'spray' && item.tipe && <Badge className="text-[9px] bg-green-50 text-green-700 border-green-200">{item.tipe}</Badge>}
                  {item._type === 'bahan-cair' && item.kategori && <Badge className="text-[9px] bg-purple-50 text-purple-700 border-purple-200">{item.kategori}</Badge>}
                  {/* Currency Badge */}
                  {isEur ? (
                    <Badge className="text-[9px] font-black bg-blue-50 text-blue-700 border border-blue-300 shadow-none">EUR (€)</Badge>
                  ) : isUsd ? (
                    <Badge className="text-[9px] font-black bg-amber-50 text-[#8C6208] border border-amber-300 shadow-none">USD ($)</Badge>
                  ) : (
                    <Badge className="text-[9px] font-black bg-emerald-50 text-emerald-800 border border-emerald-300 shadow-none">IDR (Rp)</Badge>
                  )}
                  {item._isEmptyItem && <Badge className="text-[9px] bg-amber-100 text-amber-700 border-amber-200">Stok Habis</Badge>}
                  {isPpnExclude ? (
                    <Badge className="text-[9px] bg-rose-50 text-rose-700 border-rose-200 shadow-none font-bold">Exclude PPN</Badge>
                  ) : (
                    <Badge className="text-[9px] bg-emerald-50 text-emerald-700 border-emerald-200 shadow-none font-bold">Include PPN</Badge>
                  )}
                  {isValas && valasUnit != null && (
                    <Badge className="text-[9px] bg-blue-50 text-blue-800 border-blue-300 shadow-none font-bold">
                      {valasSymbol}{fmt(valasUnit)}/{item.satuan || 'pcs'} <span className="opacity-75 font-normal text-[8px] ml-0.5">(Rate PO: Rp {fmt(frozenRate)})</span>
                    </Badge>
                  )}
                </div>
              </div>
            </div>

            {/* Brand Dependencies */}
            {item._brands && item._brands.length > 0 && (
              <div className="mt-2 text-[10px] text-slate-500 bg-slate-50/50 px-2.5 py-1.5 rounded-lg border border-slate-100 leading-tight flex gap-1.5 items-center">
                <div className="shrink-0 text-[#8C6208]">
                  {item._isManualInput || item._ticket?.is_manual || item.is_manual ? <PenTool className="w-3 h-3" /> : <Package2 className="w-3 h-3" />}
                </div>
                <div>
                  {item._isManualInput || item._ticket?.is_manual || item.is_manual ? (
                    <span className="font-bold text-slate-700">Input Manual</span>
                  ) : (
                    <>
                      <span className="font-bold text-slate-700">Dibutuhkan oleh PO:</span><br/>
                      <span className="text-slate-600">{item._brands.join(', ')}</span>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Compact PO Progress Bar (HANYA untuk Item Kebutuhan PO Klien) */}
            {item._brands && item._brands.length > 0 && (() => {
              const statusStr = item._ticket?.status || status || 'Harus Dibeli';
              let currentStep = 1;
              if (['Dalam Permintaan', 'Menunggu Pembayaran', 'Sudah Dibayar'].includes(statusStr)) currentStep = 2;
              else if (statusStr === 'Dalam Pengiriman') currentStep = 3;
              else if (statusStr === 'Sudah Datang') currentStep = 4;

              const stepLabels = ['Dibutuhkan PO', 'Naik PO Supplier', 'Dalam Pengiriman', 'Tiba di Gudang'];

              return (
                <div className="mt-2.5 mb-2 flex items-center justify-between bg-[#F9F5EC] px-3 py-1.5 rounded-xl border border-[#E6D5B8] text-[10px]">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <Clock className="w-3.5 h-3.5 text-[#8C6208] shrink-0" />
                    <span className="font-bold text-slate-700 truncate">Progres PO:</span>
                    <span className="font-black text-[#8C6208] truncate">{currentStep}/4 ({stepLabels[currentStep-1]})</span>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); setSelectedProgressItem({ ...item, _currentStep: currentStep, _statusStr: statusStr, _matchingReq: matchingReq }); }}
                    className="font-black text-[#8C6208] hover:underline flex items-center gap-0.5 shrink-0 ml-2"
                  >
                    Lacak Detail <ChevronRight className="w-3 h-3" />
                  </button>
                </div>
              );
            })()}

            {/* Stock Info */}
            <div className="grid grid-cols-2 gap-2 text-xs bg-slate-50 rounded-lg p-3">
              <div>
                <p className="text-[9px] text-slate-400 font-bold uppercase">Stok Gudang</p>
                <p className={`font-semibold ${(item.stok || 0) <= 0 ? 'text-rose-600' : 'text-slate-700'}`}>
                  {fmtDecimal(item.stok || 0)} {item.satuan}
                </p>
              </div>
              <div>
                <p className="text-[9px] text-slate-400 font-bold uppercase">Kebutuhan PO</p>
                <p className="font-semibold text-slate-700">{item._reqQty ? `${fmtDecimal(item._reqQty)} ${item.satuan}` : '-'}</p>
              </div>
            </div>
            
            {/* ── Qty Input Section ── */}
            <div 
              className={`mt-3 border rounded-xl p-3 ${item._isEmptyItem ? 'bg-amber-50/50 border-amber-100' : 'bg-rose-50/50 border-rose-100'}`}
              onClick={(e) => { if (isSelectionMode) e.stopPropagation(); }}
            >
              <div className="flex items-center justify-between mb-2">
                <p className={`text-[9px] font-bold uppercase flex items-center gap-1 ${item._isEmptyItem ? 'text-amber-500' : 'text-rose-500'}`}>
                  {item._isEmptyItem ? <Bot className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />} Harus Dibeli
                </p>
                <div className="flex items-center gap-1">
                  {paymentBadge}
                </div>
              </div>

              <div className="flex items-center gap-2">
                {isConfirmed ? (
                  <div className="flex items-center gap-2 flex-1">
                    <div className="flex-1">
                      <p className={`font-black text-xl ${item._isEmptyItem ? 'text-amber-700' : 'text-rose-700'}`}>
                        {fmtDecimal(displayQty)} <span className={`text-xs font-bold ${item._isEmptyItem ? 'text-amber-500' : 'text-rose-500'}`}>{item.satuan}</span>
                      </p>
                    </div>
                    <button
                      onClick={() => { if (!isSelectionMode) editQtyForItem(item); }}
                      disabled={isSelectionMode}
                      className={`p-1.5 rounded-lg bg-white border border-slate-200 text-slate-500 transition-colors ${isSelectionMode ? 'opacity-50 cursor-not-allowed' : 'hover:text-[#8C6208] hover:border-[#8C6208]/30'}`}
                      title="Edit jumlah"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 flex-1">
                    <div className="flex-1 relative">
                      <Input
                        type="text"
                        inputMode="decimal"
                        value={displayQty}
                        onChange={(e) => setQtyForItem(item, e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') confirmQtyForItem(item); }}
                        disabled={isSelectionMode}
                        className={`h-10 text-lg font-bold pr-12 border-2 rounded-xl ${item._isEmptyItem ? 'border-amber-300 focus:border-amber-500' : 'border-rose-300 focus:border-rose-500'} ${isSelectionMode ? 'opacity-50' : ''}`}
                      />
                      <span className={`absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold ${item._isEmptyItem ? 'text-amber-500' : 'text-rose-400'}`}>{item.satuan}</span>
                    </div>
                    <button
                      onClick={() => { if (!isSelectionMode) confirmQtyForItem(item); }}
                      disabled={isSelectionMode}
                      className={`p-2 rounded-xl text-white transition-colors shadow-sm ${item._isEmptyItem ? 'bg-amber-500 hover:bg-amber-600' : 'bg-rose-500 hover:bg-rose-600'} ${isSelectionMode ? 'opacity-50 cursor-not-allowed' : ''}`}
                      title="Konfirmasi jumlah"
                    >
                      <Check className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>

              {/* Est. Biaya */}
              {showPrice && (
                <div className="flex justify-between items-center mt-2 pt-2 border-t border-dashed border-slate-200/60">
                  <span className={`text-[9px] font-bold uppercase ${item._isEmptyItem ? 'text-amber-500' : 'text-rose-400'}`}>Est. Biaya</span>
                  <div className="text-right">
                    {totalValas != null && (
                      <span className="text-[11px] font-extrabold text-blue-700 mr-1.5 inline-block">
                        {valasSymbol} {fmt(totalValas)} <span className="opacity-60 text-slate-400 font-normal">≈</span>
                      </span>
                    )}
                    <span className={`font-bold text-sm ${item._isEmptyItem ? 'text-amber-700' : 'text-rose-700'}`}>
                      Rp {fmt(totalCost)}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className={`px-4 py-3 border-t border-slate-100 flex items-center justify-between bg-slate-50/50 mt-auto transition-colors`}>
            <div className="flex items-center gap-2">
              <button 
                type="button"
                className="text-[10px] text-[#8C6208] font-black cursor-pointer hover:bg-amber-100/80 bg-amber-50 px-2.5 py-1 rounded-lg border border-amber-200 flex items-center gap-1 transition-all shadow-xs"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedProgressItem(item);
                }}
              >
                <Clock className="w-3.5 h-3.5 text-[#8C6208]" /> Progres PO
              </button>
              <span 
                className="text-[10px] text-slate-500 font-medium cursor-pointer hover:text-slate-700 flex items-center gap-0.5"
                onClick={(e) => {
                  e.stopPropagation();
                  if (!isSelectionMode) navigate(`/office/purchasing/stok-detail?id=${item.id}&tipe=${item._type}`);
                }}
              >
                Detail Stok <ChevronRight className="w-3 h-3" />
              </span>
            </div>
            {status === 'Harus Dibeli' && !isSelectionMode && (
              <button
                onClick={(e) => { e.stopPropagation(); handleOpenPengajuan([item], groupSupplier); }}
                className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-wider px-3 py-1.5 rounded-xl bg-[#8C6208] hover:bg-[#6B4A06] text-white transition-colors shadow-sm"
              >
                <Send className="w-3 h-3" /> Pengajuan
              </button>
            )}
            {(status === 'Dalam Permintaan') && (
              paymentBadge ? paymentBadge : <Badge className="text-[9px] bg-amber-100 text-amber-700 border-amber-200 shadow-none">Menunggu Finance</Badge>
            )}
            {status === 'Dalam Pengiriman' && !isSelectionMode && (
              <div className="flex items-center gap-2">
                {paymentBadge}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setEkspedisiItem([item]);
                    setEkspedisiNama('');
                    setShowEkspedisiModal(true);
                  }}
                  className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-wider px-3 py-1.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white transition-colors shadow-sm"
                >
                  <PackageCheck className="w-3 h-3" /> Sudah Sampai
                </button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <>
    <div className="space-y-6 text-[#302621] print:hidden">
      <div>
        <h1 className="text-3xl font-black text-slate-800 tracking-tight">Item Sudah PO</h1>
        <p className="text-xs text-slate-500 mt-1">Sistem otomatis menampilkan material & kemasan yang sudah diproses atau sedang dalam perjalanan (Dalam Pengiriman, Sudah Dibayar, dll).</p>
      </div>

      {asaInsights.length > 0 && showAsa && (
        <AsaAssistant 
          insights={asaInsights}
          title="Rekomendasi Asa"
          theme="indigo"
        />
      )}

      {/* ── BATCH ACTION BAR ── */}
      {isSelectionMode && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[100] w-[95%] max-w-3xl bg-white/95  shadow-[0_10px_40px_rgba(0,0,0,0.12)] border border-slate-200/60 rounded-2xl p-3 animate-in slide-in-from-top-4 fade-in duration-300">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <button onClick={selectAll} className="flex items-center gap-2 text-sm font-bold text-slate-700 hover:text-slate-900">
                {selectedIds.size === deficits.length ? <CheckSquare className="w-5 h-5 text-[#8C6208]" /> : <Square className="w-5 h-5 text-slate-400" />}
                <span className="hidden sm:inline">Pilih Semua</span>
              </button>
              <div className="h-6 w-px bg-slate-200" />
              <span className="text-sm font-black text-[#8C6208] bg-amber-50 px-3 py-1 rounded-lg border border-amber-200">
                {selectedIds.size} Terpilih
              </span>
            </div>

            {/* Batch Fill Qty */}
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1 bg-slate-50 border border-slate-200 rounded-xl px-2 py-1">
                <Input
                  type="number"
                  placeholder="Isi qty..."
                  value={batchFillQty}
                  onChange={setBatchFillQty}
                  className="w-24 h-7 text-xs border-0 bg-transparent p-0 focus-visible:ring-0"
                />
                <Button 
                  size="sm" 
                  onClick={handleBatchFillQty} 
                  disabled={!batchFillQty}
                  className="h-6 px-2 text-[10px] bg-[#8C6208] hover:bg-[#6B4A06] text-white rounded-lg"
                >
                  Isi Semua
                </Button>
              </div>

              <div className="h-6 w-px bg-slate-200" />

              {/* Batch Status Update */}
              <div className="w-[160px]">
                <Select
                  value={batchTargetStatus}
                  onValueChange={(val) => {
                    if (val) {
                      setBatchTargetStatus(val);
                      setShowBatchStatusModal(true);
                    }
                  }}
                >
                  <SelectTrigger className="h-8 text-[10px] font-black uppercase tracking-wider border-[#8C6208]/30 bg-[#8C6208]/5 text-[#8C6208] hover:bg-[#8C6208]/10 rounded-xl transition-colors">
                    <SelectValue placeholder="Update Status..." />
                  </SelectTrigger>
                  <SelectContent className="z-[200]">
                    {STATUS_LIST.map(s => <SelectItem key={s} value={s} className="text-[10px] font-bold">{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <Button 
                onClick={() => {
                  const itemsToBuy = deficits.filter(d => selectedIds.has(getItemKey(d)));
                  if (itemsToBuy.length > 0) {
                    const firstItemKey = getItemKey(itemsToBuy[0]);
                    const group = supplierGrouped.find(g => g.items.some(it => getItemKey(it) === firstItemKey));
                    handleOpenPengajuan(itemsToBuy, group?.supplier || null);
                  }
                }}
                className="h-8 px-3 text-[10px] font-black uppercase tracking-wider bg-[#8C6208] hover:bg-[#6B4A06] text-white rounded-xl shadow-sm gap-1 transition-all"
              >
                <FileText className="w-3.5 h-3.5" />
                Buat PO
              </Button>

              {/* Batch Delete / Ignore */}
              <Button 
                onClick={handleBatchIgnoreOrDelete}
                className="h-8 px-3 text-[10px] font-black uppercase tracking-wider bg-rose-600 hover:bg-rose-700 text-white rounded-xl shadow-sm gap-1 transition-all"
                title="Hapus semua item terpilih dari daftar"
              >
                <Trash2 className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Hapus</span>
              </Button>

              <Button onClick={clearSelection} variant="ghost" size="icon" className="h-8 w-8 rounded-xl hover:bg-slate-100 text-slate-500">
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center items-center py-20">
          <div className="w-8 h-8 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : deficits.length === 0 ? (
        <div className="text-center py-20 bg-white border border-slate-200 rounded-3xl">
          <CheckCircle className="w-12 h-12 mx-auto text-emerald-400 mb-3" />
          <p className="text-sm font-semibold text-slate-500">Semua stok aman!</p>
          <p className="text-xs text-slate-400 mt-1">Tidak ada bahan yang mengalami defisit berdasarkan PO berjalan.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* ── FILTERS ── */}
          <div className="flex flex-col sm:flex-row gap-3 items-center bg-white p-3 rounded-2xl border border-slate-200 shadow-sm">
            <div className="flex-1 w-full relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Cari nama item, kategori, atau vendor..."
                value={mainSearchQuery}
                onChange={(e) => setMainSearchQuery(e.target.value)}
                className="pl-9 h-10 border-slate-200 bg-slate-50 focus-visible:bg-white rounded-xl"
              />
            </div>
            <div className="w-full sm:w-[250px]">
              <Select value={selectedSupplierFilter} onValueChange={setSelectedSupplierFilter}>
                <SelectTrigger className="h-10 border-slate-200 bg-slate-50 focus:bg-white rounded-xl font-bold text-slate-700">
                  <SelectValue placeholder="Pilih Supplier / Vendor..." />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  <SelectItem value="all" className="font-bold">Semua Supplier & Vendor</SelectItem>
                  {supplierGrouped.map(group => (
                    <SelectItem key={group.key} value={group.key} className="font-medium">
                      {group.label} ({group.items.length})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          {/* ── Render Fullset Cards ── */}
          {fullsetCards.length > 0 && (
            <div>
              <h3 className="text-xs font-black text-emerald-700 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                <Package2 className="w-4 h-4" /> Pembelian Fullset
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {fullsetCards.map((group, idx) => {
                  const { fullset, items } = group;
                  const statusWeight = { 'Harus Dibeli': 0, 'Dalam Permintaan': 1, 'Sudah Dibayar': 2, 'Dalam Pengiriman': 3, 'Sudah Datang': 4 };
                  const minWeight = items.length > 0 ? Math.min(...items.map(i => statusWeight[i._ticket?.status || 'Harus Dibeli'])) : 0;
                  const status = Object.keys(statusWeight).find(k => statusWeight[k] === minWeight) || 'Harus Dibeli';
                  let matchingReq = null;
                  if (items.length > 0) {
                    const firstItem = items[0];
                    matchingReq = requests.find(r => isRequestMatchingItem(r, firstItem.id, firstItem._type));
                  }
                  let paymentBadge = null;
                  if (matchingReq && status !== 'Harus Dibeli') {
                    if (matchingReq.metode_pembayaran === 'Tempo') {
                      paymentBadge = <Badge className="text-[9px] bg-orange-100 text-orange-700 border-orange-200 shadow-none">Tempo</Badge>;
                    } else {
                      if (matchingReq.status === 'Sudah Dibayar') {
                        paymentBadge = <Badge className="text-[9px] bg-emerald-100 text-emerald-700 border-emerald-200 shadow-none">Sudah Dibayar</Badge>;
                      } else {
                        paymentBadge = <Badge className="text-[9px] bg-rose-100 text-rose-700 border-rose-200 shadow-none">Belum Dibayar</Badge>;
                      }
                    }
                  }
                  return (
                    <Card key={`fs-${fullset.id}-${idx}`} className="border-emerald-200/80 hover:border-emerald-500/40 hover:shadow-md bg-emerald-50/10 transition-all flex flex-col">
                      <CardContent className="p-0 flex flex-col h-full">
                        <div className={`px-4 py-2 flex justify-between items-center border-b border-emerald-100/50 ${status === 'Harus Dibeli' ? 'bg-amber-50/50' : 'bg-emerald-50/50'}`}>
                          <span className="text-[10px] font-black text-emerald-700 uppercase tracking-wider flex items-center gap-1.5">
                            <Package2 className="w-3.5 h-3.5" /> Pembelian Fullset
                          </span>
                          <Badge className={`text-[9px] uppercase tracking-wider ${getStatusColor(status)} shadow-none`}>{status}</Badge>
                        </div>
                        <div className="p-4 flex-1">
                          <h4 className="font-bold text-slate-800 text-base">{fullset.nama}</h4>
                          <p className="text-[10px] text-slate-500 mb-3 font-medium">Pembelian dilakukan dalam bentuk satu set packaging lengkap.</p>
                          <div className="space-y-1.5 mb-2">
                            {items.map(comp => (
                              <div key={`${comp._type}-${comp.id}`} className="flex items-center justify-between bg-white border border-slate-100 rounded-lg p-2 text-xs shadow-sm">
                                <span className="font-medium text-slate-700 truncate mr-2 flex-1">{comp.nama}</span>
                                <span className="font-bold text-rose-600 bg-rose-50 px-2 py-0.5 rounded shrink-0">Kurang {fmtDecimal(comp._deficitQty)} {comp.satuan}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                        <div className="px-4 py-3 border-t border-emerald-100 flex items-center justify-between bg-emerald-50/30 mt-auto">
                          <span className="text-xs font-bold text-emerald-800">Rp {fmt(items.reduce((s, it) => s + (it.harga_rupiah || 0), 0))} / set</span>
                          {status === 'Harus Dibeli' && !isSelectionMode && (
                            <button
                              onClick={(e) => { e.stopPropagation(); handleOpenPengajuan(items); }}
                              className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-wider px-4 py-1.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white transition-colors shadow-sm"
                            >
                              <Send className="w-3 h-3" /> Beli Fullset
                            </button>
                          )}
                          {(status === 'Dalam Permintaan') && (
                            paymentBadge ? paymentBadge : <Badge className="text-[9px] bg-amber-100 text-amber-700 border-amber-200 shadow-none">Menunggu Finance</Badge>
                          )}
                          {status === 'Dalam Pengiriman' && !isSelectionMode && (
                            <div className="flex items-center gap-2">
                              {paymentBadge}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEkspedisiItem(items);
                                  setEkspedisiNama('');
                                  setShowEkspedisiModal(true);
                                }}
                                className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-wider px-3 py-1.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white transition-colors shadow-sm"
                              >
                                <PackageCheck className="w-3 h-3" /> Sudah Sampai
                              </button>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Render Supplier-Grouped Items ── */}
          {filteredSupplierGrouped.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map((group) => {
            const harusBeliItems = group.items.filter(it => (it._ticket?.status || 'Harus Dibeli') === 'Harus Dibeli');
            const totalEstimasi = group.items.reduce((s, it) => s + ((getQtyForItem(it).qty || it._deficitQty || 0) * (it.harga_rupiah || 0)), 0);

            return (
              <Card key={group.key} className="border-slate-200/80 shadow-sm overflow-hidden bg-white">
                <CardContent className="p-0">
                  {/* Supplier Header */}
                  <div className="bg-gradient-to-r from-[#302621] to-[#4a3a2e] px-5 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
                        <Building2 className="w-5 h-5 text-white/80" />
                      </div>
                      <div>
                        <h3 className="font-black text-white text-base">{group.label}</h3>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-[10px] text-white/50 font-bold uppercase tracking-wider">
                            {group.items.length} item • Est. Rp {fmt(totalEstimasi)}
                          </span>
                          {group.supplier && (
                            <Badge className="text-[8px] bg-white/10 text-white/70 border-white/20 shadow-none">
                              Database ✓
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    {harusBeliItems.length > 0 && !isSelectionMode && (
                      <button
                        onClick={() => handleOpenPengajuan(harusBeliItems, group.supplier)}
                        className="flex items-center gap-2 text-[10px] font-black uppercase tracking-wider px-4 py-2 rounded-xl bg-[#D4AF37] hover:bg-[#b8962e] text-[#302621] transition-colors shadow-md"
                      >
                        <FileText className="w-3.5 h-3.5" /> Buat PO ({harusBeliItems.length})
                      </button>
                    )}
                  </div>

                  {/* Supplier Items Grid */}
                  <div className="p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {group.items.map((item, idx) => renderDeficitCard(item, idx, group.supplier))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}

          {/* ── Pagination Controls ── */}
          {filteredSupplierGrouped.length > 0 && (
            <div className="px-6 py-4 border border-slate-100 rounded-2xl bg-white flex flex-wrap items-center justify-between gap-4 mt-6">
              <div className="flex items-center gap-3">
                <span className="text-[11px] font-bold text-slate-500">Row per Page</span>
                <div className="w-[80px]">
                  <Select value={String(itemsPerPage)} onValueChange={(val) => { setItemsPerPage(Number(val)); setCurrentPage(1); }}>
                    <SelectTrigger className="h-8 text-[11px] font-bold text-slate-700 bg-slate-50 border-slate-200/80 rounded-xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[5, 10, 25, 50].map(n => (
                        <SelectItem key={n} value={String(n)} className="text-[11px] font-bold">{n}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="flex items-center gap-1.5">
                <button 
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:bg-slate-50 hover:text-[#8C6208] disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-slate-400 transition-all font-bold text-sm"
                >
                  &lt;
                </button>
                
                {(() => {
                  const totalPages = Math.ceil(filteredSupplierGrouped.length / itemsPerPage);
                  const pages = [];
                  for(let i=1; i<=totalPages; i++) {
                    if (i === 1 || i === totalPages || (i >= currentPage - 1 && i <= currentPage + 1)) {
                      pages.push(
                        <button
                          key={i}
                          onClick={() => setCurrentPage(i)}
                          className={`w-8 h-8 flex items-center justify-center rounded-lg text-[11px] font-black transition-all ${
                            currentPage === i 
                              ? 'bg-[#8C6208] text-white shadow-md shadow-[#8C6208]/30' 
                              : 'text-slate-500 hover:bg-[#F9F5EC] hover:text-[#8C6208]'
                          }`}
                        >
                          {i}
                        </button>
                      );
                    } else if (i === currentPage - 2 || i === currentPage + 2) {
                      pages.push(<span key={i} className="text-slate-400 text-[10px] px-1">...</span>);
                    }
                  }
                  return pages;
                })()}
                
                <button 
                  onClick={() => setCurrentPage(p => Math.min(Math.ceil(filteredSupplierGrouped.length / itemsPerPage), p + 1))}
                  disabled={currentPage >= Math.ceil(filteredSupplierGrouped.length / itemsPerPage)}
                  className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:bg-slate-50 hover:text-[#8C6208] disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-slate-400 transition-all font-bold text-sm"
                >
                  &gt;
                </button>
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Floating Back to Top Button */}
      <button 
        onClick={scrollToTop}
        className="fixed bottom-[80px] md:bottom-8 right-6 md:right-8 z-50 w-12 h-12 bg-[#8C6208] hover:bg-[#6A4A06] text-white rounded-full shadow-xl shadow-[#8C6208]/30 flex items-center justify-center transition-transform hover:-translate-y-1 print:hidden border border-[#D4AF37]/30"
        title="Kembali ke Atas"
      >
        <ChevronUp className="w-6 h-6" />
      </button>

      {/* ── BATCH STATUS CONFIRMATION MODAL ── */}
      {showBatchStatusModal && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40  animate-in fade-in duration-200">
          <div className="bg-white rounded-3xl shadow-2xl w-[95%] max-w-lg p-6 animate-in zoom-in-95 duration-200">
            <div className="text-center mb-5">
              <div className={`w-16 h-16 mx-auto rounded-2xl flex items-center justify-center mb-3 ${
                batchTargetStatus === 'Sudah Datang' ? 'bg-emerald-100 text-emerald-600' :
                batchTargetStatus === 'Dalam Pengiriman' ? 'bg-indigo-100 text-indigo-600' :
                batchTargetStatus === 'Sudah Dibayar' ? 'bg-blue-100 text-blue-600' :
                'bg-amber-100 text-amber-600'
              }`}>
                {getStatusIcon(batchTargetStatus) || <ShoppingCart className="w-8 h-8" />}
              </div>
              <h3 className="text-xl font-black text-slate-800">Konfirmasi Update Status</h3>
              <p className="text-sm text-slate-500 mt-1">
                Update <span className="font-bold text-[#8C6208]">{selectedIds.size} item</span> ke status:
              </p>
              <Badge className={`mt-2 text-sm px-4 py-1 ${getStatusColor(batchTargetStatus)}`}>{batchTargetStatus}</Badge>
            </div>

            {batchTargetStatus === 'Sudah Datang' && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4 mb-4">
                <p className="text-xs text-emerald-700 font-semibold flex items-center gap-1.5 mb-2">
                  <PackageCheck className="w-4 h-4" /> Barang yang akan masuk ke stok:
                </p>
                <div className="space-y-1.5 max-h-40 overflow-y-auto">
                  {deficits.filter(d => selectedIds.has(getItemKey(d))).map(item => {
                    const q = getQtyForItem(item);
                    return (
                      <div key={getItemKey(item)} className="flex items-center justify-between text-xs bg-white rounded-lg px-3 py-2 border border-emerald-100">
                        <span className="font-medium text-slate-700 truncate">{item.nama}</span>
                        <span className="font-bold text-emerald-700 whitespace-nowrap ml-2">+{fmtDecimal(q.qty)} {item.satuan}</span>
                      </div>
                    );
                  })}
                </div>
                <p className="text-[10px] text-emerald-600 mt-2 font-medium">
                  ✅ Stok akan otomatis bertambah & tercatat di Barang Masuk sebagai 1 batch
                </p>
              </div>
            )}

            <div className="flex gap-3">
              <Button
                onClick={() => { setShowBatchStatusModal(false); setBatchTargetStatus(''); }}
                variant="outline"
                className="flex-1 h-11 rounded-xl font-bold"
                disabled={isBatchProcessing}
              >
                Batal
              </Button>
              <Button
                onClick={handleBatchStatusSubmit}
                disabled={isBatchProcessing}
                className={`flex-1 h-11 rounded-xl font-bold gap-2 ${
                  batchTargetStatus === 'Sudah Datang' ? 'bg-emerald-600 hover:bg-emerald-700' :
                  batchTargetStatus === 'Sudah Dibayar' ? 'bg-blue-600 hover:bg-blue-700' :
                  'bg-[#8C6208] hover:bg-[#6B4A06]'
                } text-white`}
              >
                {isBatchProcessing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                Ya, Update!
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ── ASA ARRIVAL CONFIRMATION MODAL ── */}
      {showEkspedisiModal && ekspedisiItem && ekspedisiItem.length > 0 && (
        <AsaArrivalConfirmModal
          isOpen={showEkspedisiModal}
          onClose={() => {
            setShowEkspedisiModal(false);
            setEkspedisiItem(null);
          }}
          requestData={{
            po_number: generateRefNumber(ekspedisiItem[0]),
            nama_perusahaan: ekspedisiItem[0].vendor || ekspedisiItem[0].supplier || 'Supplier',
            pic: member?.full_name || 'Purchasing Staff',
            batch_items: ekspedisiItem.map(it => {
              const qtyData = getQtyForItem(it);
              const buyQty = qtyData.qty !== undefined ? Number(String(qtyData.qty).replace(',', '.')) : it._deficitQty;
              return {
                id: it.id,
                nama: it.nama,
                qty: buyQty,
                satuan: it.satuan || 'pcs',
                type: it._type,
                kategori: it.kategori || it._type,
                harga: it.harga_rupiah || it.harga_beli || 0
              };
            })
          }}
          inventoryMaster={{
            bahanCairList,
            botolList,
            tutupList,
            sprayList,
            fullsetList,
            itemTambahanList
          }}
          isProcessing={isProcessingArrival}
          onConfirm={async ({ ekspedisi, catatan, items }) => {
            setIsProcessingArrival(true);
            const itemsProcessed = [];
            try {
              for (const it of items) {
                const rawItem = ekspedisiItem.find(ei => String(ei.id) === String(it.id)) || it;
                const buyQty = it.incomingQty || 0;

                // 1. Update stok
                await updateStock(rawItem, buyQty);

                // 2. Update/Create ticket
                if (rawItem._ticket) {
                  await updateTicketMut.mutateAsync({ id: rawItem._ticket.id, data: { status: 'Sudah Datang', qty_to_buy: buyQty } });
                  
                  // Find related PurchasingRequest if in 'Dalam Pengiriman'
                  const relatedReq = requests.find(r => 
                    r.status === 'Dalam Pengiriman' && 
                    isRequestMatchingItem(r, rawItem.id, rawItem._type)
                  );
                  if (relatedReq) {
                    await base44.entities.PurchasingRequest.update(relatedReq.id, { status: 'Barang Datang' });
                  }
                } else {
                  await createTicketMut.mutateAsync({ material_id: rawItem.id, material_type: rawItem._type, material_name: rawItem.nama, qty_to_buy: buyQty, status: 'Sudah Datang' });
                }

                itemsProcessed.push({
                  inventory_item_id: rawItem.id,
                  nama_barang: it.nama,
                  kode: rawItem.kode || '',
                  kategori: it.kategori || it.masterCategory || rawItem._type,
                  satuan: it.satuan || 'pcs',
                  jumlah: buyQty,
                  vendor: rawItem._ticket?.vendor || rawItem.vendor || rawItem.supplier || ''
                });
              }

              // 3. Catat Barang Masuk resmi di database
              if (itemsProcessed.length > 0) {
                const firstItem = ekspedisiItem[0];
                const refNumber = generateRefNumber(firstItem);
                const relatedTicket = firstItem._ticket;

                await base44.entities.BarangMasuk.create({
                  tanggal: new Date().toISOString().split('T')[0],
                  nomor_referensi: refNumber,
                  supplier: itemsProcessed[0].vendor || 'Supplier',
                  ekspedisi: ekspedisi || 'Ekspedisi Vendor / Langsung',
                  ongkir: relatedTicket?.ongkir || 0,
                  ongkir_dibayar_oleh: 'Perusahaan',
                  catatan: catatan || `Barang masuk via Purchasing — Ekspedisi: ${ekspedisi || 'Langsung'}`,
                  pic: member?.full_name || member?.email || 'Purchasing Staff',
                  input_by: member?.email || 'System',
                  items: itemsProcessed
                });
              }

              // 4. Invalidate queries
              queryClient.invalidateQueries({ queryKey: ['barang-masuk'] });
              queryClient.invalidateQueries({ queryKey: ['purchasing-requests'] });
              queryClient.invalidateQueries({ queryKey: ['bahan-cair'] });
              queryClient.invalidateQueries({ queryKey: ['botol'] });
              queryClient.invalidateQueries({ queryKey: ['tutup'] });
              queryClient.invalidateQueries({ queryKey: ['spray'] });
              queryClient.invalidateQueries({ queryKey: ['fullset'] });
              queryClient.invalidateQueries({ queryKey: ['item-tambahan'] });

              // 5. Success popup
              setShowSuccessPopup({
                status: 'Sudah Datang',
                items: itemsProcessed,
                count: itemsProcessed.length
              });

              setShowEkspedisiModal(false);
              setEkspedisiItem(null);
              clearSelection();
            } catch (err) {
              toast.error('Gagal memproses kedatangan: ' + err.message);
            } finally {
              setIsProcessingArrival(false);
            }
          }}
        />
      )}

      {/* ── SUCCESS POPUP ── */}
      {showSuccessPopup && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center bg-black/40  animate-in fade-in duration-200">
          <div className="bg-white rounded-3xl shadow-2xl w-[95%] max-w-md p-8 text-center animate-in zoom-in-95 duration-300">
            
            {/* Animated success icon */}
            <div className="relative w-24 h-24 mx-auto mb-5">
              <div className={`w-24 h-24 rounded-full flex items-center justify-center animate-bounce ${
                showSuccessPopup.status === 'Sudah Datang' ? 'bg-emerald-100' : 'bg-blue-100'
              }`}>
                {showSuccessPopup.status === 'Sudah Datang' ? (
                  <PackageCheck className="w-12 h-12 text-emerald-500" />
                ) : (
                  <CheckCircle className="w-12 h-12 text-blue-500" />
                )}
              </div>
              {/* Sparkles decoration */}
              <Sparkles className="w-6 h-6 text-amber-400 absolute -top-1 -right-1 animate-pulse" />
              <Sparkles className="w-4 h-4 text-amber-300 absolute -bottom-0 -left-1 animate-pulse delay-150" />
            </div>

            <h3 className="text-2xl font-black text-slate-800 mb-2">
              {showSuccessPopup.status === 'Sudah Datang' ? 'Barang Berhasil Diterima! 🎉' : 'Status Berhasil Diupdate! ✅'}
            </h3>
            <p className="text-sm text-slate-500 mb-4">
              {showSuccessPopup.count} item telah diupdate ke status <span className="font-bold">{showSuccessPopup.status}</span>
            </p>

            {showSuccessPopup.status === 'Sudah Datang' && (
              <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4 mb-5 text-left">
                <p className="text-[10px] font-black text-slate-400 uppercase mb-2">Ringkasan Barang Masuk</p>
                <div className="space-y-1.5 max-h-32 overflow-y-auto">
                  {showSuccessPopup.items.map((it, i) => (
                    <div key={i} className="flex items-center justify-between text-xs">
                      <span className="text-slate-600 truncate">{it.nama_barang}</span>
                      <span className="font-bold text-emerald-600 whitespace-nowrap ml-2">+{fmtDecimal(it.jumlah)} {it.satuan}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-3 pt-2 border-t border-dashed border-slate-200 text-[10px] text-slate-500 flex items-center gap-1">
                  <CheckCircle className="w-3 h-3 text-emerald-500" />
                  Tercatat di Barang Masuk sebagai 1 batch
                </div>
              </div>
            )}

            <Button
              onClick={() => setShowSuccessPopup(null)}
              className="w-full h-12 rounded-2xl font-bold text-base bg-[#8C6208] hover:bg-[#6B4A06] text-white shadow-lg"
            >
              Tutup
            </Button>
          </div>
        </div>
      )}
      {/* ── PURCHASE ORDER MODAL ── */}
      {showPengajuanModal && pengajuanItem && pengajuanItem.length > 0 && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50  animate-in fade-in duration-200 p-4">
          <div className="bg-slate-100 rounded-3xl shadow-2xl w-full max-w-3xl animate-in zoom-in-95 duration-200 overflow-hidden max-h-[90vh] flex flex-col">
            {/* Modal Top Bar */}
            <div className="bg-[#302621] px-5 py-3 flex items-center justify-between shrink-0">
              <div className="flex items-center gap-2 text-white">
                <FileText className="w-4 h-4" />
                <span className="text-sm font-black uppercase tracking-wider">Purchase Order</span>
              </div>
              <button onClick={() => setShowPengajuanModal(false)} className="text-white/60 hover:text-white p-1">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Standard Form Content */}
            <div className="flex-1 overflow-y-auto p-4 sm:p-6 bg-slate-50/50">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                 {/* Kiri: Info Supplier & Bank */}
                 <div className="space-y-4">
                    <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm space-y-3">
                       <h4 className="text-xs font-bold text-slate-800 border-b border-slate-100 pb-2 mb-2">Informasi Supplier</h4>
                       
                       <div className="relative">
                          <label className="text-[10px] font-bold text-slate-500 mb-1 block">Cari Database Supplier</label>
                          <Input placeholder="Ketik nama sales / bank..." value={supplierSearch} onChange={(val) => { setSupplierSearch(val); setShowSupplierDropdown(true); }} onFocus={() => setShowSupplierDropdown(true)} className="h-9 text-xs" />
                          {showSupplierDropdown && (() => {
                            const q = String(supplierSearch || '').toLowerCase();
                            const filtered = suppliers.filter(s => { if (!q) return true; return String(s.nama_sales || '').toLowerCase().includes(q) || String(s.bank || '').toLowerCase().includes(q) || String(s.atas_nama || '').toLowerCase().includes(q); });
                            if (filtered.length === 0) return null;
                            return (
                              <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-40 overflow-y-auto">
                                {filtered.map(s => (
                                  <button key={s.id} type="button" onClick={() => { setPengajuanNamaSales(s.nama_sales || ''); setPengajuanNamaPerusahaan(s.nama_perusahaan || ''); setPengajuanNPWP(s.npwp || ''); setPengajuanBank(s.bank || ''); setPengajuanNoRek(s.no_rekening || ''); setPengajuanNamaRek(s.atas_nama || ''); setPengajuanMetode(s.metode_pembayaran || 'Cash'); setPengajuanOngkirMode(s.ongkir_mode || 'transfer'); setPengajuanSupplierPhone(s.phone || ''); setPengajuanSupplierAddress(s.alamat || ''); setSupplierSearch(s.nama_sales); setShowSupplierDropdown(false); }} className="w-full text-left px-3 py-2 text-[10px] hover:bg-slate-50 border-b border-slate-100 last:border-0">
                                    <p className="font-bold text-slate-800">{s.nama_sales}</p>
                                    <p className="text-[9px] text-slate-400">{s.bank} - {s.atas_nama}</p>
                                  </button>
                                ))}
                              </div>
                            );
                          })()}
                       </div>

                       <div>
                          <label className="text-[10px] font-bold text-slate-500 mb-1 block">Nama Perusahaan Supplier</label>
                          <Input placeholder="Contoh: PT Supplier Makmur" value={pengajuanNamaPerusahaan} onChange={(e) => setPengajuanNamaPerusahaan(e.target.value)} className="h-9 text-xs font-semibold text-[#8C6208]" />
                       </div>
                       
                       <div>
                          <label className="text-[10px] font-bold text-slate-500 mb-1 block">NPWP Supplier</label>
                          <Input placeholder="Contoh: 12.345.678.9-012.000" value={pengajuanNPWP} onChange={(e) => setPengajuanNPWP(e.target.value)} className="h-9 text-xs" />
                       </div>
                       
                       <div>
                          <label className="text-[10px] font-bold text-slate-500 mb-1 block">Nama Sales / PIC Supplier *</label>
                          <Input placeholder="Contoh: Budi" value={pengajuanNamaSales} onChange={(e) => setPengajuanNamaSales(e.target.value)} className="h-9 text-xs" />
                       </div>
                       
                       <div>
                          <label className="text-[10px] font-bold text-slate-500 mb-1 block">No. Telepon Supplier</label>
                          <Input placeholder="Contoh: 08123456789" value={pengajuanSupplierPhone} onChange={(e) => setPengajuanSupplierPhone(e.target.value)} className="h-9 text-xs" />
                       </div>

                       <div>
                          <label className="text-[10px] font-bold text-slate-500 mb-1 block">Alamat Supplier</label>
                          <Input placeholder="Contoh: Jl. Sudirman No 1..." value={pengajuanSupplierAddress} onChange={(e) => setPengajuanSupplierAddress(e.target.value)} className="h-9 text-xs" />
                       </div>
                       
                       <div className="grid grid-cols-2 gap-3">
                          <div>
                             <label className="text-[10px] font-bold text-slate-500 mb-1 block">Metode Pembayaran</label>
                             <div className="flex bg-slate-100 p-1 rounded-lg">
                                <button onClick={() => setPengajuanMetode('Cash')} className={`flex-1 py-1.5 text-[10px] font-bold rounded-md transition-all ${pengajuanMetode === 'Cash' ? 'bg-white shadow-sm text-[#8C6208]' : 'text-slate-500 hover:bg-slate-200'}`}>Cash</button>
                                <button onClick={() => setPengajuanMetode('Tempo')} className={`flex-1 py-1.5 text-[10px] font-bold rounded-md transition-all ${pengajuanMetode === 'Tempo' ? 'bg-white shadow-sm text-[#8C6208]' : 'text-slate-500 hover:bg-slate-200'}`}>Tempo</button>
                             </div>
                          </div>
                          {pengajuanMetode === 'Tempo' && (
                             <div>
                                <label className="text-[10px] font-bold text-slate-500 mb-1 block">Jatuh Tempo *</label>
                                <CustomDatePicker value={pengajuanTempo} onChange={setPengajuanTempo} className="h-9 text-xs" />
                             </div>
                          )}
                       </div>
                    </div>

                    <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm space-y-3">
                       <h4 className="text-xs font-bold text-slate-800 border-b border-slate-100 pb-2 mb-2">Informasi Pembayaran Bank</h4>
                       
                       <div>
                          <label className="text-[10px] font-bold text-slate-500 mb-1 block">Bank Tujuan *</label>
                          <Input list="bank-list-po" placeholder="BCA / Mandiri..." value={pengajuanBank} onChange={(e) => setPengajuanBank(e.target.value)} className="h-9 text-xs" />
                          <datalist id="bank-list-po"><option value="BCA" /><option value="Mandiri" /><option value="BRI" /><option value="BNI" /></datalist>
                       </div>
                       
                       <div>
                          <label className="text-[10px] font-bold text-slate-500 mb-1 block">Nomor Rekening *</label>
                          <Input placeholder="1234567890" value={pengajuanNoRek} onChange={(e) => setPengajuanNoRek(e.target.value)} className="h-9 text-xs font-mono font-bold tracking-wider" />
                       </div>
                       
                       <div>
                          <label className="text-[10px] font-bold text-slate-500 mb-1 block">Atas Nama *</label>
                          <Input placeholder="A.n Pemilik Rekening" value={pengajuanNamaRek} onChange={(e) => setPengajuanNamaRek(e.target.value)} className="h-9 text-xs" />
                       </div>
                    </div>
                 </div>

                 {/* Kanan: Ringkasan Pemesan & Item */}
                 <div className="space-y-4">
                    <div className="bg-[#8C6208]/5 border border-[#8C6208]/20 rounded-xl p-4 shadow-sm space-y-3">
                       <h4 className="text-xs font-black tracking-wider text-[#8C6208] border-b border-[#8C6208]/10 pb-2 mb-2">Informasi Perusahaan Pemesan</h4>
                       <p className="text-[9px] text-slate-500 mb-2 leading-tight">Data ini akan tercetak di PDF Purchase Order sebagai pihak pembeli/pemesan.</p>

                       <div className="mb-3 relative">
                           <label className="text-[10px] font-bold text-slate-600 mb-1 block">Pilih dari Data Pemesan (Opsional)</label>
                           <Input 
                             placeholder="Ketik nama perusahaan..." 
                             value={pemesanSearch} 
                             onChange={(e) => { setPemesanSearch(e.target.value); setShowPemesanDropdown(true); }} 
                             onFocus={() => setShowPemesanDropdown(true)} 
                             className="h-9 text-xs" 
                           />
                           {showPemesanDropdown && (() => {
                             const q = String(pemesanSearch || '').toLowerCase();
                             const filtered = dataPemesanList.filter(p => {
                               if (!q) return true;
                               return String(p.nama_perusahaan || '').toLowerCase().includes(q) || String(p.nama_pic || '').toLowerCase().includes(q);
                             });
                             
                             return (
                               <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-40 overflow-y-auto">
                                 {dataPemesanList.length === 0 ? (
                                   <div className="px-3 py-2 text-[10px] text-slate-400">Belum ada profil (Tambahkan di menu Data Pemesan)</div>
                                 ) : filtered.length === 0 ? (
                                   <div className="px-3 py-2 text-[10px] text-slate-400">Tidak ditemukan</div>
                                 ) : (
                                   filtered.map(p => (
                                     <button 
                                       key={p.id} 
                                       type="button" 
                                       onClick={() => {
                                         setPengajuanPemesanNama(p.nama_perusahaan || '');
                                         setPengajuanPemesanNPWP(p.npwp || '');
                                         setPengajuanPemesanAlamat(p.alamat || '');
                                         setPengajuanPemesanPIC(p.nama_pic || '');
                                         setPengajuanPemesanTelp(p.no_telp_pic || '');
                                         setPemesanSearch(p.nama_perusahaan || '');
                                         setShowPemesanDropdown(false);
                                       }} 
                                       className="w-full text-left px-3 py-2 text-[10px] hover:bg-slate-50 border-b border-slate-100 last:border-0"
                                     >
                                       <p className="font-bold text-slate-800">{p.nama_perusahaan}</p>
                                       <p className="text-[9px] text-slate-400">PIC: {p.nama_pic || '-'}</p>
                                     </button>
                                   ))
                                 )}
                               </div>
                             );
                           })()}
                         </div>

                       <div>
                          <label className="text-[10px] font-bold text-slate-600 mb-1 block">Nama Perusahaan Kita</label>
                          <Input placeholder="PT / CV..." value={pengajuanPemesanNama} onChange={(e) => setPengajuanPemesanNama(e.target.value)} className="h-9 text-xs font-semibold" />
                       </div>

                       <div className="grid grid-cols-2 gap-3">
                          <div>
                             <label className="text-[10px] font-bold text-slate-600 mb-1 block">NPWP Perusahaan</label>
                             <Input placeholder="NPWP..." value={pengajuanPemesanNPWP} onChange={(e) => setPengajuanPemesanNPWP(e.target.value)} className="h-9 text-xs" />
                          </div>
                          <div>
                             <label className="text-[10px] font-bold text-slate-600 mb-1 block">No. Telp PIC</label>
                             <Input placeholder="Telepon..." value={pengajuanPemesanTelp} onChange={(e) => setPengajuanPemesanTelp(e.target.value)} className="h-9 text-xs" />
                          </div>
                       </div>

                       <div>
                          <label className="text-[10px] font-bold text-slate-600 mb-1 block">Alamat Pengiriman</label>
                          <Input placeholder="Alamat lengkap pengiriman..." value={pengajuanPemesanAlamat} onChange={(e) => setPengajuanPemesanAlamat(e.target.value)} className="h-9 text-xs" />
                       </div>

                       <div>
                          <label className="text-[10px] font-bold text-slate-600 mb-1 block">Nama PIC Kita</label>
                          <Input placeholder="Nama lengkap..." value={pengajuanPemesanPIC} onChange={(e) => setPengajuanPemesanPIC(e.target.value)} className="h-9 text-xs" />
                       </div>

                       {/* Pilih Keterangan & Syarat PO */}
                       <div className="pt-2 border-t border-[#8C6208]/20">
                          <label className="text-[10px] font-black text-[#8C6208] mb-1.5 block uppercase tracking-wider">
                            Pilih Keterangan & Syarat PO (PDF)
                          </label>
                          <div className="space-y-1.5 max-h-[130px] overflow-y-auto pr-1 mb-2 bg-white/60 p-2 rounded-lg border border-[#8C6208]/10">
                            {PREDEFINED_SYARAT_OPTIONS.map((opt, sIdx) => (
                              <label key={sIdx} className="flex items-start gap-2 text-[10px] text-slate-700 cursor-pointer hover:text-slate-900">
                                <Checkbox 
                                  checked={pengajuanSyaratList.includes(opt)}
                                  onCheckedChange={(checked) => {
                                    if (checked) {
                                      setPengajuanSyaratList([...pengajuanSyaratList, opt]);
                                    } else {
                                      setPengajuanSyaratList(pengajuanSyaratList.filter(s => s !== opt));
                                    }
                                  }}
                                  className="mt-0.5 h-3.5 w-3.5"
                                />
                                <span className="leading-tight">{opt}</span>
                              </label>
                            ))}
                          </div>
                          <div className="flex gap-1.5">
                            <Input 
                              placeholder="+ Tambah syarat khusus PO..." 
                              value={customSyaratInput} 
                              onChange={(e) => setCustomSyaratInput(e.target.value)}
                              className="h-8 text-[10px] flex-1"
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && customSyaratInput.trim()) {
                                  e.preventDefault();
                                  if (!pengajuanSyaratList.includes(customSyaratInput.trim())) {
                                    setPengajuanSyaratList([...pengajuanSyaratList, customSyaratInput.trim()]);
                                  }
                                  setCustomSyaratInput('');
                                }
                              }}
                            />
                            <Button 
                              type="button" 
                              size="sm" 
                              variant="outline" 
                              className="h-8 text-[10px] px-2 text-[#8C6208] border-[#8C6208]/30 hover:bg-[#8C6208]/10"
                              onClick={() => {
                                if (customSyaratInput.trim()) {
                                  if (!pengajuanSyaratList.includes(customSyaratInput.trim())) {
                                    setPengajuanSyaratList([...pengajuanSyaratList, customSyaratInput.trim()]);
                                  }
                                  setCustomSyaratInput('');
                                }
                              }}
                            >
                              Tambah
                            </Button>
                          </div>
                       </div>
                    </div>

                    <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
                       <h4 className="text-xs font-bold text-slate-800 border-b border-slate-100 pb-2 mb-3">Daftar Item ({pengajuanItem.length})</h4>
                       
                       <div className="space-y-2 max-h-[180px] overflow-y-auto pr-1">
                          {pengajuanItem.map((item, idx) => {
                             const qty = getQtyForItem(item).qty || item._deficitQty || 0;
                             let basePrice = 0;
                             if (item.ppn_type === 'exclude' && item.harga_sebelum_ppn && parseFloat(item.harga_sebelum_ppn) > 0) {
                               basePrice = parseFloat(item.harga_sebelum_ppn);
                             } else {
                               basePrice = parseFloat(item.harga_rupiah || item.harga_beli || 0);
                             }
                             
                             const isExcludePpn = item.ppn_type === 'exclude';
                             const ppnPct = item.ppn_percentage || 11;
                             const ppnAmount = isExcludePpn ? (basePrice * qty) * (ppnPct / 100) : 0;
                             
                             return (
                               <div key={idx} className="flex justify-between items-center text-xs p-2.5 bg-slate-50 rounded-lg border border-slate-100">
                                 <div className="flex-1 overflow-hidden pr-2">
                                    <div className="flex items-center gap-2 mb-0.5">
                                      <p className="font-bold text-slate-700 truncate">{item.nama}</p>
                                      {isExcludePpn ? (
                                        <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-rose-100 text-rose-700 whitespace-nowrap">
                                          Belum PPN (+Rp {fmt(ppnAmount)})
                                        </span>
                                      ) : (
                                        <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 whitespace-nowrap">
                                          Include PPN
                                        </span>
                                      )}
                                    </div>
                                    <p className="text-[10px] text-slate-500">{fmtDecimal(qty)} {item.satuan} × Rp {fmt(basePrice)}</p>
                                 </div>
                                 <div className="font-bold text-slate-800 whitespace-nowrap">Rp {fmt(basePrice * qty)}</div>
                               </div>
                             )
                          })}
                       </div>
                    </div>

                    <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm space-y-3">
                       <h4 className="text-xs font-bold text-slate-800 border-b border-slate-100 pb-2 mb-2">Biaya Tambahan</h4>
                       
                       <div className="grid grid-cols-2 gap-3">
                          <div className="col-span-2">
                             <label className="text-[10px] font-bold text-slate-500 mb-1 block">Mode Ongkos Kirim</label>
                             <div className="flex bg-slate-100 p-1 rounded-lg">
                                {[{mode:'transfer',label:'Transfer'},{mode:'petty_cash',label:'Petty Cash'},{mode:'free_ongkir',label:'Free Ongkir'}].map(o => (
                                  <button key={o.mode} onClick={() => setPengajuanOngkirMode(o.mode)} className={`flex-1 py-1.5 text-[10px] font-bold rounded-md transition-all ${pengajuanOngkirMode === o.mode ? 'bg-white shadow-sm text-slate-800' : 'text-slate-500 hover:bg-slate-200'}`}>{o.label}</button>
                                ))}
                             </div>
                          </div>
                          {pengajuanOngkirMode !== 'free_ongkir' && (
                             <div className="col-span-2">
                                <label className="text-[10px] font-bold text-slate-500 mb-1 block">Biaya Ongkir (Rp)</label>
                                <Input type="number" placeholder="0" value={pengajuanOngkir} onChange={(e) => setPengajuanOngkir(e.target.value)} className="h-9 text-xs" />
                             </div>
                          )}
                          <div className="col-span-2">
                             <label className="text-[10px] font-bold text-slate-500 mb-1 block">Catatan Internal (Opsional)</label>
                             <Input placeholder="Detail vendor atau link produk..." value={pengajuanCatatan} onChange={(e) => setPengajuanCatatan(e.target.value)} className="h-9 text-xs" />
                          </div>
                       </div>
                    </div>

                    {/* Grand Total */}
                    {showPrice && (() => {
                      let subtotalBarang = 0; let ppnTotal = 0; let hasPpn = false; let mainPpnPct = 11;
                      pengajuanItem.forEach(it => {
                        const qty = getQtyForItem(it).qty || it._deficitQty || 0;
                        let basePrice = 0; const isPpnExclude = it.ppn_type === 'exclude'; const ppnPct = parseFloat(it.ppn_percentage) || 11;
                        if (isPpnExclude && it.harga_sebelum_ppn && parseFloat(it.harga_sebelum_ppn) > 0) {
                          basePrice = parseFloat(it.harga_sebelum_ppn); hasPpn = true; mainPpnPct = ppnPct;
                          const finalPrice = parseFloat(it.harga_rupiah || it.harga_beli || 0); ppnTotal += (finalPrice - basePrice) * qty;
                        } else {
                          basePrice = parseFloat(it.harga_rupiah || it.harga_beli || 0);
                          if (isPpnExclude) { hasPpn = true; mainPpnPct = ppnPct; ppnTotal += (basePrice * (ppnPct / 100)) * qty; }
                        }
                        subtotalBarang += basePrice * qty;
                      });
                      const ongkirVal = pengajuanOngkirMode === 'free_ongkir' ? 0 : pengajuanOngkirMode === 'transfer' ? (parseFloat(pengajuanOngkir) || 0) : 0;
                      const grandTotal = subtotalBarang + ppnTotal + ongkirVal;
                      return (
                         <div className="bg-[#302621] rounded-2xl p-5 text-white shadow-xl relative overflow-hidden">
                           <div className="absolute -right-10 -top-10 w-32 h-32 bg-white/5 rounded-full blur-2xl pointer-events-none"></div>
                           <div className="flex justify-between items-center text-xs text-white/70 mb-2 relative z-10">
                             <span>Subtotal Barang</span><span className="font-semibold text-white">Rp {fmt(subtotalBarang)}</span>
                           </div>
                           {hasPpn && (
                             <div className="flex justify-between items-center text-xs text-white/70 mb-2 relative z-10">
                               <span>PPN {mainPpnPct}%</span><span className="font-semibold text-white">Rp {fmt(ppnTotal)}</span>
                             </div>
                           )}
                           {ongkirVal > 0 && (
                             <div className="flex justify-between items-center text-xs text-white/70 mb-3 relative z-10">
                               <span>Ongkir ({pengajuanOngkirMode})</span><span className={`font-semibold ${pengajuanOngkirMode === 'petty_cash' ? 'line-through text-white/40' : 'text-white'}`}>Rp {fmt(ongkirVal)}</span>
                             </div>
                           )}
                           <div className="flex justify-between items-center border-t border-white/20 pt-3 mt-1 relative z-10">
                             <span className="font-bold text-sm tracking-wider">GRAND TOTAL</span>
                             <span className="font-black text-xl text-[#E6D5B8]">Rp {fmt(grandTotal)}</span>
                           </div>
                         </div>
                      );
                    })()}
                 </div>
              </div>
            </div>

            {/* Modal Bottom Bar */}
            <div className="bg-white p-5 border-t border-slate-200 flex flex-col sm:flex-row justify-end gap-3 shrink-0">
              <Button variant="outline" onClick={() => setShowPengajuanModal(false)} className="h-11 px-8 rounded-xl font-bold border-slate-200">
                Batal
              </Button>
              <Button onClick={handleSubmitPengajuan} disabled={isSubmittingPengajuan} className="h-11 px-8 rounded-xl font-bold bg-[#8C6208] hover:bg-[#6B4A06] text-white shadow-md">
                {isSubmittingPengajuan ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                Kirim Pengajuan
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ── PRINT POPUP SUCCESS ── */}
      {showPrintPopup && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center bg-black/50  animate-in fade-in duration-200">
          <div className="bg-white rounded-3xl shadow-2xl w-[95%] max-w-md p-8 text-center animate-in zoom-in-95 duration-300 relative overflow-hidden">
            <div className="absolute -top-10 -right-10 w-32 h-32 bg-emerald-500/10 rounded-full blur-3xl"></div>
            
            <div className="relative w-20 h-20 mx-auto mb-6">
              <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center">
                <CheckCircle className="w-10 h-10 text-emerald-500" />
              </div>
              <Sparkles className="w-6 h-6 text-emerald-400 absolute -top-1 -right-1 animate-pulse" />
            </div>

            <h3 className="text-2xl font-black text-slate-800 mb-2">Pengajuan Terkirim!</h3>
            <p className="text-sm text-slate-500 mb-8 leading-relaxed">
              PO berhasil dikirim ke Finance. Anda dapat mengunduh dokumen PO sekarang atau mencetaknya nanti di menu Pengajuan Pembelian.
            </p>

            <div className="flex flex-col gap-3">
              <Button
                onClick={() => {
                  const req = showPrintPopup;
                  setPrintData(req);
                  setShowPrintPopup(null);
                  const origTitle = document.title;
                  const cleanPo = String(req?.po_number || req?.id || 'PO-Purchasing').replace(/[/\\?%*:|"<>]/g, '-').trim();
                  const cleanVendor = String(req?.supplier_name || req?.nama_supplier || req?.vendor || '').replace(/[/\\?%*:|"<>]/g, '-').trim();
                  document.title = `${cleanPo}${cleanVendor ? ' - ' + cleanVendor : ''}`;
                  setTimeout(() => {
                    window.print();
                    setTimeout(() => { document.title = origTitle; }, 1000);
                  }, 300);
                }}
                className="w-full h-12 rounded-2xl font-bold text-base bg-[#8C6208] hover:bg-[#6B4A06] text-white shadow-lg flex items-center gap-2"
              >
                <Printer className="w-5 h-5" /> Download PO (PDF)
              </Button>
              <Button
                onClick={() => setShowPrintPopup(null)}
                variant="outline"
                className="w-full h-12 rounded-2xl font-bold text-base border-slate-200 text-slate-600 hover:bg-slate-50"
              >
                Lewati (Skip)
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* HIDDEN PRINT COMPONENT */}
    </div>
    <POPrint data={printData} />
      {/* ── Modal Audit Trail & Progres PO ── */}
      {selectedProgressItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 -xs animate-in fade-in duration-200">
          <div className="bg-white rounded-3xl max-w-lg w-full overflow-hidden shadow-2xl border border-slate-100 flex flex-col max-h-[90vh]">
            {/* Modal Header */}
            <div className="p-5 bg-gradient-to-r from-amber-900 via-[#8C6208] to-amber-800 text-white flex justify-between items-center">
              <div className="flex items-center gap-2.5">
                <div className="p-2 bg-white/10 rounded-xl">
                  <Clock className="w-5 h-5 text-amber-300" />
                </div>
                <div>
                  <h3 className="font-extrabold text-base leading-tight">Progres & Timeline Material PO</h3>
                  <p className="text-xs text-amber-200/90 font-medium mt-0.5">{selectedProgressItem.nama}</p>
                </div>
              </div>
              <button 
                onClick={() => setSelectedProgressItem(null)}
                className="p-1.5 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Modal Content Body */}
            <div className="p-6 overflow-y-auto space-y-6">
              {/* Material & PO Demand Summary */}
              <div className="bg-amber-50/70 p-4 rounded-2xl border border-amber-200/60 space-y-2.5">
                <div className="flex justify-between items-center text-xs">
                  <span className="font-bold text-slate-600">Total Kebutuhan PO:</span>
                  <span className="font-black text-[#8C6208] text-sm">{selectedProgressItem._reqQty} {selectedProgressItem.satuan}</span>
                </div>
                {selectedProgressItem._brands && selectedProgressItem._brands.length > 0 && (
                  <div className="text-xs border-t border-amber-200/50 pt-2 mt-2">
                    {selectedProgressItem._isManualInput || selectedProgressItem._ticket?.is_manual || selectedProgressItem.is_manual ? (
                      <span className="font-bold text-slate-700 block mb-1">Sumber: <span className="text-[#8C6208]">Input Manual</span></span>
                    ) : (
                      <>
                        <span className="font-bold text-slate-700 block mb-1">Dibutuhkan oleh PO:</span>
                        <div className="bg-white/80 p-2 rounded-xl text-slate-600 text-[11px] font-medium leading-relaxed">
                          {Array.isArray(selectedProgressItem._brands) ? selectedProgressItem._brands.join(', ') : selectedProgressItem._brands}
                        </div>
                      </>
                    )}
                  </div>
                )}
                {/* Total Process Duration Lead Time */}
                {(() => {
                  const startDate = selectedProgressItem._poDates?.[0] || selectedProgressItem._ticket?.created_date;
                  const endDate = selectedProgressItem._currentStep >= 4 ? (selectedProgressItem._ticket?.updated_date || selectedProgressItem._ticket?.updated_at) : null;
                  const durationStr = calculateLeadTime(startDate, endDate);
                  if (!durationStr) return null;

                  return (
                    <div className="flex justify-between items-center text-xs border-t border-amber-200/50 pt-2.5">
                      <span className="font-bold text-slate-700 flex items-center gap-1.5">
                        <Timer className="w-4 h-4 text-[#8C6208]" /> 
                        {selectedProgressItem._currentStep >= 4 ? 'Total Durasi Pengadaan Selesai:' : 'Durasi Pengadaan Berjalan:'}
                      </span>
                      <span className={`text-[11px] font-black px-2.5 py-0.5 rounded-full shadow-xs flex items-center gap-1 ${selectedProgressItem._currentStep >= 4 ? 'bg-emerald-600 text-white' : 'bg-amber-600 text-white'}`}>
                        {durationStr}
                      </span>
                    </div>
                  );
                })()}
              </div>

              {/* Large Stepper Timeline */}
              <div className="space-y-5 relative pl-6 border-l-2 border-slate-200 ml-3">
                {/* Step 1 */}
                <div className="relative flex items-start gap-3">
                  <div className={`absolute -left-[35px] top-0.5 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${selectedProgressItem._currentStep >= 1 ? 'bg-[#8C6208] text-white shadow-sm' : 'bg-slate-200 text-slate-500'}`}>
                    {selectedProgressItem._currentStep > 1 ? <Check className="w-3.5 h-3.5" /> : '1'}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-extrabold text-sm text-slate-800">
                        {selectedProgressItem._isManualInput || selectedProgressItem._ticket?.is_manual 
                          ? '1. Input Manual Pengajuan' 
                          : selectedProgressItem._isEmptyItem 
                            ? '1. Stok Gudang Habis (Restock)' 
                            : '1. Dibutuhkan PO Klien'}
                      </span>
                      <span className="text-[10px] bg-amber-100 text-amber-900 font-extrabold px-2 py-0.5 rounded border border-amber-200/60 shadow-xs">
                        {selectedProgressItem._poDates && selectedProgressItem._poDates.length > 0 
                          ? selectedProgressItem._poDates.join(', ') 
                          : (selectedProgressItem._ticket?.created_date 
                              ? new Date(selectedProgressItem._ticket.created_date).toLocaleDateString('id-ID', { day:'numeric', month:'short', year:'numeric' }) 
                              : '10 Agu 2026')}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      {selectedProgressItem._isManualInput || selectedProgressItem._ticket?.is_manual 
                        ? 'Item ditambahkan secara manual oleh pengguna untuk kebutuhan/pengajuan khusus.' 
                        : selectedProgressItem._isEmptyItem 
                          ? 'Stok fisik di gudang terdeteksi 0 dan otomatis tercatat butuh pengadaan restock.' 
                          : 'Pesanan Klien dibuat & membutuhkan bahan ini untuk proses produksi.'}
                    </p>
                  </div>
                </div>

                {/* Step 2 */}
                <div className="relative flex items-start gap-3 pt-1">
                  <div className={`absolute -left-[35px] top-1.5 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${selectedProgressItem._currentStep >= 2 ? 'bg-[#8C6208] text-white shadow-sm' : 'bg-slate-200 text-slate-500'}`}>
                    {selectedProgressItem._currentStep > 2 ? <Check className="w-3.5 h-3.5" /> : '2'}
                  </div>
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-extrabold text-sm text-slate-800">2. Naik PO Supplier / Pengajuan</span>
                      <span className={`text-[10px] font-extrabold px-2 py-0.5 rounded shadow-xs ${selectedProgressItem._currentStep >= 2 ? 'bg-amber-100 text-amber-800 border border-amber-200' : 'bg-slate-100 text-slate-400'}`}>
                        {selectedProgressItem._currentStep >= 2 ? (selectedProgressItem._matchingReq?.created_date ? new Date(selectedProgressItem._matchingReq.created_date).toLocaleDateString('id-ID', { day:'numeric', month:'short', year:'numeric' }) : (selectedProgressItem._ticket?.created_date ? new Date(selectedProgressItem._ticket.created_date).toLocaleDateString('id-ID', { day:'numeric', month:'short', year:'numeric' }) : 'Diajukan')) : 'Belum Diajukan'}
                      </span>
                      {selectedProgressItem._currentStep >= 2 && calculateLeadTime(selectedProgressItem._poDates?.[0] || selectedProgressItem._ticket?.created_date, selectedProgressItem._matchingReq?.created_date || selectedProgressItem._ticket?.created_date) && (
                        <span className="text-[10px] text-amber-800 bg-amber-50 border border-amber-200/80 px-2 py-0.5 rounded-full font-bold">
                          ⚡ Respon Pengajuan: {calculateLeadTime(selectedProgressItem._poDates?.[0] || selectedProgressItem._ticket?.created_date, selectedProgressItem._matchingReq?.created_date || selectedProgressItem._ticket?.created_date)}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      {selectedProgressItem._currentStep >= 2 ? `Tim Purchasing telah mengajukan pembelian ke supplier ${selectedProgressItem.vendor || ''}.` : 'Menunggu tim Purchasing mengajukan pembelian ke supplier.'}
                    </p>
                  </div>
                </div>

                {/* Step 3 */}
                <div className="relative flex items-start gap-3 pt-1">
                  <div className={`absolute -left-[35px] top-1.5 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${selectedProgressItem._currentStep >= 3 ? 'bg-[#8C6208] text-white shadow-sm' : 'bg-slate-200 text-slate-500'}`}>
                    {selectedProgressItem._currentStep > 3 ? <Check className="w-3.5 h-3.5" /> : '3'}
                  </div>
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-extrabold text-sm text-slate-800">3. Dalam Pengiriman (OTW)</span>
                      <span className={`text-[10px] font-extrabold px-2 py-0.5 rounded shadow-xs ${selectedProgressItem._currentStep >= 3 ? 'bg-purple-100 text-purple-800 border border-purple-200' : 'bg-slate-100 text-slate-400'}`}>
                        {selectedProgressItem._currentStep >= 3 ? (selectedProgressItem._ticket?.updated_date || selectedProgressItem._ticket?.updated_at ? new Date(selectedProgressItem._ticket.updated_date || selectedProgressItem._ticket.updated_at).toLocaleDateString('id-ID', { day:'numeric', month:'short', year:'numeric' }) : 'Dalam Pengiriman') : 'Belum Dikirim'}
                      </span>
                      {selectedProgressItem._currentStep >= 3 && calculateLeadTime(selectedProgressItem._matchingReq?.created_date || selectedProgressItem._ticket?.created_date, selectedProgressItem._ticket?.updated_date || selectedProgressItem._ticket?.updated_at) && (
                        <span className="text-[10px] text-purple-800 bg-purple-50 border border-purple-200/80 px-2 py-0.5 rounded-full font-bold">
                          💳 Proses Vendor: {calculateLeadTime(selectedProgressItem._matchingReq?.created_date || selectedProgressItem._ticket?.created_date, selectedProgressItem._ticket?.updated_date || selectedProgressItem._ticket?.updated_at)}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      {selectedProgressItem._currentStep >= 3 ? 'Supplier telah mengonfirmasi barang dalam pengiriman ekspedisi.' : 'Menunggu supplier mengirimkan barang.'}
                    </p>
                  </div>
                </div>

                {/* Step 4 */}
                <div className="relative flex items-start gap-3 pt-1">
                  <div className={`absolute -left-[35px] top-1.5 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${selectedProgressItem._currentStep >= 4 ? 'bg-emerald-600 text-white shadow-sm' : 'bg-slate-200 text-slate-500'}`}>
                    {selectedProgressItem._currentStep >= 4 ? <Check className="w-3.5 h-3.5" /> : '4'}
                  </div>
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-extrabold text-sm text-slate-800">4. Tiba di Gudang & Siap</span>
                      <span className={`text-[10px] font-extrabold px-2 py-0.5 rounded shadow-xs ${selectedProgressItem._currentStep >= 4 ? 'bg-emerald-100 text-emerald-800 border border-emerald-200' : 'bg-slate-100 text-slate-400'}`}>
                        {selectedProgressItem._currentStep >= 4 ? (selectedProgressItem._ticket?.updated_date || selectedProgressItem._ticket?.updated_at ? new Date(selectedProgressItem._ticket.updated_date || selectedProgressItem._ticket.updated_at).toLocaleDateString('id-ID', { day:'numeric', month:'short', year:'numeric' }) : 'Selesai Tiba') : 'Belum Datang'}
                      </span>
                      {selectedProgressItem._currentStep >= 4 && calculateLeadTime(selectedProgressItem._ticket?.updated_date || selectedProgressItem._ticket?.created_date, selectedProgressItem._ticket?.updated_at) && (
                        <span className="text-[10px] text-emerald-800 bg-emerald-50 border border-emerald-200/80 px-2 py-0.5 rounded-full font-bold">
                          🚚 Durasi Pengiriman: {calculateLeadTime(selectedProgressItem._ticket?.updated_date || selectedProgressItem._ticket?.created_date, selectedProgressItem._ticket?.updated_at)}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      {selectedProgressItem._currentStep >= 4 ? 'Barang telah tiba di gudang dan fisik stok bertambah.' : 'Menunggu konfirmasi kedatangan fisik di gudang.'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-4 bg-slate-50 border-t border-slate-100 flex justify-end">
              <Button onClick={() => setSelectedProgressItem(null)} className="bg-[#8C6208] text-white hover:bg-amber-900 rounded-xl text-xs font-bold px-5">
                Tutup
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
